import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, formatCurrency, formatBoolean } from '../utils/dateUtils';
import PaginatedTable from './common/PaginatedTable';
import CustomSelect from './CustomSelect';
import { 
  Avatar, 
  Box,
  Button,
  Breadcrumbs,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Description as ContractsIcon,
  Assessment as AuditIcon,
  AccountBalance as TransactionsIcon,
  TrendingUp as ForecastIcon,
  People as AccountsIcon,
  Settings as SettingsIcon,
  Group as UsersIcon,
  Edit as EditIcon,
  NavigateNext as NavigateNextIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Payment as PaymentIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  ApartmentOutlined as ApartmentIcon,
  Business as BusinessIcon,
  CancelOutlined as CancelOutlinedIcon,
  CheckCircleOutlined as CheckCircleOutlinedIcon,
} from '@mui/icons-material';
import '../styles/Dashboard.css';
import '../styles/ContractDetails.css';
import { usePageVisibilityContext } from '../contexts/PageVisibilityContext';
import usePageVisibility from '../hooks/usePageVisibility';
import MainLayout from './MainLayout';

// Import Recharts components at the top of the file
import { 
  ResponsiveContainer, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ReferenceLine,
  Legend
} from 'recharts';

// Add global styling function at the top of the component after formStyles
const textFieldProps = {
  InputProps: {
    style: { 
      color: 'black'
    }
  }
};

// Add a consistent style object for all CustomSelect components
const customSelectStyle = {
  color: 'black'
};

// Direct debug check for text color in TextField components
const debugStyle = {
  position: 'absolute',
  top: '2px',
  right: '5px',
  fontSize: '10px',
  color: 'red',
  zIndex: 999
};

// Focused fix for text field values - only changes text color
const textFieldInputProps = {
  style: { 
    color: '#000000',
    backgroundColor: 'white',
    border: 'none',
    opacity: 1
  }
};

// Define a simple, minimal style approach
const basicTextFieldStyle = {
  backgroundColor: 'white',
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#8A42EC',
    }
  },
  '& .MuiInputBase-input': {
    color: 'black'
  }
};

// Remove all the complex styling and just use a basic switch
const basicSwitchStyle = {
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#8A42EC'
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#8A42EC'
  }
};

// Style for the autorenewal box - removing the border
const autorenewalBoxStyle = {
  display: 'flex', 
  alignItems: 'center'
};

// Style for the switch value text
const switchValueTextStyle = {
  marginLeft: '10px',
  color: '#555',
  fontWeight: 500
};

export const ContractDetails = () => {
  const { contractId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { lockPageRefresh, unlockPageRefresh } = usePageVisibilityContext();
  const { disableRefresh, enableRefresh } = usePageVisibility();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contract, setContract] = useState(null);
  const [editedContract, setEditedContract] = useState({
    contract_name: '',
    contract_description: '',
    contract_status: '',
    contract_type: '',
    amount: '',
    monthly_points_allotment: '',
    payment_type: '',
    autorenewal: false,
    quickbooks_business_unit_id: '',
    account_manager: '',
    team_manager: '',
    priority: '',
    customer_display_type: '',
    engagement_type: '',
    hosting: false,
    initial_term_length: '',
    subsequent_term_length: '',
    notice_period: '',
    contract_start_date: '',
    link_to_slack_channel: '',
    deal_id: '',
    clickup_folder_id: ''
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [amendments, setAmendments] = useState([]);
  const [pointsSummary, setPointsSummary] = useState(null);
  const [termDetails, setTermDetails] = useState(null);
  const [financialDetails, setFinancialDetails] = useState({
    invoices: [],
    payments: [],
    credits: []
  });
  
  // Documents state
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [showAddDocument, setShowAddDocument] = useState(false);
  const [isEditingDocument, setIsEditingDocument] = useState(false);
  const [newDocument, setNewDocument] = useState({
    contract_id: contractId,
    document_url: '',
    description: '',
    contract_document_type: ''
  });
  
  // Resources state
  const [resources, setResources] = useState([]);
  const [showAddResource, setShowAddResource] = useState(false);
  const [isEditingResource, setIsEditingResource] = useState(false);
  const [newResource, setNewResource] = useState({
    contract_id: contractId,
    resource_name: '',
    resource_description: '',
    resource_link: ''
  });
  
  // Amendments state
  const [showAddAmendment, setShowAddAmendment] = useState(false);
  const [isEditingAmendment, setIsEditingAmendment] = useState(false);
  const [newAmendment, setNewAmendment] = useState({
    contract_id: contractId,
    deal_id: '',
    amendment_description: '',
    amendment_type: '',
    amount: '',
    amendment_status: '',
    document_link: ''
  });
  
  // Reference data from server
  const [businessUnits, setBusinessUnits] = useState([]);
  const [clickupUsers, setClickupUsers] = useState([]);
  const [contractStatuses, setContractStatuses] = useState([
    { value: 'pending', label: 'Pending' },
    { value: 'active', label: 'Active' },
    { value: 'canceled', label: 'Canceled' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'completed', label: 'Completed' }
  ]);
  const [contractTypes, setContractTypes] = useState([
    { value: 'recurring', label: 'Recurring' },
    { value: 'project', label: 'Project' }
  ]);
  const [displayTypes, setDisplayTypes] = useState([
    { value: 'points', label: 'Points' },
    { value: 'hours', label: 'Hours' },
    { value: 'none', label: 'None' }
  ]);
  const [engagementTypes, setEngagementTypes] = useState([
    { value: 'strategic', label: 'Strategic' },
    { value: 'tactical', label: 'Tactical' }
  ]);
  const [paymentTypes, setPaymentTypes] = useState([
    { value: 'invoice', label: 'Invoice' },
    { value: 'credit_card', label: 'Credit Card' }
  ]);
  const [priorityTiers, setPriorityTiers] = useState([
    { value: 'Tier 1', label: 'Tier 1' },
    { value: 'Tier 2', label: 'Tier 2' },
    { value: 'Tier 3', label: 'Tier 3' },
    { value: 'Tier 4', label: 'Tier 4' }
  ]);
  
  // Pagination states
  const [invoicePage, setInvoicePage] = useState(1);
  const [creditPage, setCreditPage] = useState(1);
  const [paymentPage, setPaymentPage] = useState(1);
  const rowsPerPage = 10;
  
  // Change page handlers
  const handleInvoicePageChange = (event, value) => {
    setInvoicePage(value);
  };
  
  const handleCreditPageChange = (event, value) => {
    setCreditPage(value);
  };
  
  const handlePaymentPageChange = (event, value) => {
    setPaymentPage(value);
  };
  
  // Get paginated data
  const getPaginatedData = (data, page) => {
    const startIndex = (page - 1) * rowsPerPage;
    return data.slice(startIndex, startIndex + rowsPerPage);
  };
  
  // Add debug toggle state at the top of the function (after other state variables)
  const [showDebug, setShowDebug] = useState(false);
  
  // Add state for upcoming invoice tasks
  const [upcomingInvoices, setUpcomingInvoices] = useState([]);
  
  // Add a new state to store point production data
  const [pointsProductionData, setPointsProductionData] = useState([]);
  
  // Add a new state to store monthly points data
  const [monthlyPointsData, setMonthlyPointsData] = useState({
    contractData: null,
    monthLabels: []
  });
  
  // Add a new state to store contract goals
  const [contractGoals, setContractGoals] = useState([]);

  // Status Reports settings (singleton per contract)
  const [statusSettings, setStatusSettings] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    enabled: false,
    recipients: [],
    cadence: 'weekly',
    weekly_day: 1,
    weekly_time: '09:00',
    interval_days: 14,
    timezone: 'America/New_York',
    lookback_days: 14,
    lookahead_days: 30
  });
  const [recipientsInput, setRecipientsInput] = useState('');
  
  // Fetch all contract data
  const fetchContractData = useCallback(async () => {
    if (!contractId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const apiBaseUrl = 'https://pulse.marketersindemand.com/api';
      
      // Fetch contract details
      const contractResponse = await fetch(`${apiBaseUrl}/contracts/${contractId}`);
      if (!contractResponse.ok) {
        throw new Error(`Failed to fetch contract: ${contractResponse.status}`);
      }
      const contractData = await contractResponse.json();
      
      console.log('Raw contract data received:', JSON.stringify(contractData, null, 2));
      console.log('STATUS CHECK:', { 
        status: contractData.status,
        contract_status: contractData.contract_status,
        clickup_folder_id: contractData.clickup_folder_id,
        rawStatusValue: typeof contractData.contract_status === 'string' ? contractData.contract_status : 'not a string'
      });
      
      // If contract has account_manager or team_manager, fetch the user details
      let accountManagerName = 'N/A';
      let teamManagerName = 'N/A';
      
      if (contractData.account_manager) {
        try {
          const accountManagerResponse = await fetch(`${apiBaseUrl}/clickup/users/${contractData.account_manager}`);
          console.log('Account Manager API Response:', accountManagerResponse.status);
          
          if (accountManagerResponse.ok) {
            const accountManagerData = await accountManagerResponse.json();
            console.log('Account Manager Data:', accountManagerData);
            accountManagerName = accountManagerData.username || 'N/A';
          } else {
            // Try fallback to local users endpoint
            const fallbackResponse = await fetch(`${apiBaseUrl}/contracts/clickup-users`);
            if (fallbackResponse.ok) {
              const allUsers = await fallbackResponse.json();
              const userMatch = allUsers.find(u => u.id === contractData.account_manager);
              if (userMatch) {
                accountManagerName = userMatch.username || 'N/A';
              }
            }
          }
        } catch (error) {
          console.error('Failed to fetch account manager details:', error);
        }
      }
      
      if (contractData.team_manager) {
        try {
          const teamManagerResponse = await fetch(`${apiBaseUrl}/clickup/users/${contractData.team_manager}`);
          console.log('Team Manager API Response:', teamManagerResponse.status);
          
          if (teamManagerResponse.ok) {
            const teamManagerData = await teamManagerResponse.json();
            console.log('Team Manager Data:', teamManagerData);
            teamManagerName = teamManagerData.username || 'N/A';
          } else {
            // Try fallback to local users endpoint
            const fallbackResponse = await fetch(`${apiBaseUrl}/contracts/clickup-users`);
            if (fallbackResponse.ok) {
              const allUsers = await fallbackResponse.json();
              const userMatch = allUsers.find(u => u.id === contractData.team_manager);
              if (userMatch) {
                teamManagerName = userMatch.username || 'N/A';
              }
            }
          }
        } catch (error) {
          console.error('Failed to fetch team manager details:', error);
        }
      }
      
      // Add manager names to the contract data
      contractData.accountManagerName = accountManagerName;
      contractData.teamManagerName = teamManagerName;
      
      console.log('Processed contract data:', contractData);
      setContract(contractData);
      
      // Fetch contract goals
      try {
        const goalsResponse = await fetch(`${apiBaseUrl}/contracts/${contractId}/goals`);
        if (goalsResponse.ok) {
          const goalsData = await goalsResponse.json();
          console.log('Contract goals data:', goalsData);
          setContractGoals(goalsData);
        } else {
          console.error('Failed to fetch contract goals:', goalsResponse.status);
        }
      } catch (error) {
        console.error('Failed to fetch contract goals:', error);
      }
      
      // Fetch amendments
      try {
        const amendmentsResponse = await fetch(`${apiBaseUrl}/amendments/contract/${contractId}`);
        if (amendmentsResponse.ok) {
          const amendmentsData = await amendmentsResponse.json();
          setAmendments(amendmentsData);
        }
      } catch (error) {
        console.error('Failed to fetch amendments:', error);
      }
      
      // Fetch points summary
      try {
        const pointsResponse = await fetch(`${apiBaseUrl}/contracts/${contractId}/points-summary`);
        if (pointsResponse.ok) {
          const pointsData = await pointsResponse.json();
          console.log('Points summary data:', pointsData);
          setPointsSummary(pointsData);
          
          // Also fetch monthly points data for the chart
          try {
            const monthlyPointsResponse = await fetch(`${apiBaseUrl}/contracts/${contractId}/monthly-points`);
            if (monthlyPointsResponse.ok) {
              const monthlyData = await monthlyPointsResponse.json();
              console.log('Monthly points data:', monthlyData);
              setMonthlyPointsData(monthlyData);
              
              // Create data for the chart using the monthly_points_view data
              if (monthlyData.contractData) {
                const monthlyAllotment = contractData?.monthly_points_allotment || 0;
                
                const chartData = [
                  { 
                    name: monthlyData.monthLabels[0], 
                    points: parseFloat(monthlyData.contractData.points_5_months_ago || 0),
                    isAboveTarget: parseFloat(monthlyData.contractData.points_5_months_ago || 0) >= monthlyAllotment
                  },
                  { 
                    name: monthlyData.monthLabels[1], 
                    points: parseFloat(monthlyData.contractData.points_4_months_ago || 0),
                    isAboveTarget: parseFloat(monthlyData.contractData.points_4_months_ago || 0) >= monthlyAllotment
                  },
                  { 
                    name: monthlyData.monthLabels[2], 
                    points: parseFloat(monthlyData.contractData.points_3_months_ago || 0),
                    isAboveTarget: parseFloat(monthlyData.contractData.points_3_months_ago || 0) >= monthlyAllotment
                  },
                  { 
                    name: monthlyData.monthLabels[3], 
                    points: parseFloat(monthlyData.contractData.points_2_months_ago || 0),
                    isAboveTarget: parseFloat(monthlyData.contractData.points_2_months_ago || 0) >= monthlyAllotment
                  },
                  { 
                    name: monthlyData.monthLabels[4], 
                    points: parseFloat(monthlyData.contractData.points_last_month || 0),
                    isAboveTarget: parseFloat(monthlyData.contractData.points_last_month || 0) >= monthlyAllotment
                  },
                  { 
                    name: monthlyData.monthLabels[5], 
                    points: parseFloat(monthlyData.contractData.points_this_month || 0),
                    isAboveTarget: parseFloat(monthlyData.contractData.points_this_month || 0) >= monthlyAllotment
                  }
                ];
                
                setPointsProductionData(chartData);
              }
            } else {
              console.error('Failed to fetch monthly points data:', monthlyPointsResponse.status);
            }
          } catch (error) {
            console.error('Error fetching monthly points data:', error);
          }
        }
      } catch (error) {
        console.error('Failed to fetch points summary:', error);
      }
      
      // Fetch term details
      try {
        const termsResponse = await fetch(`${apiBaseUrl}/contracts/${contractId}/terms`);
        if (termsResponse.ok) {
          const termsData = await termsResponse.json();
          setTermDetails(termsData);
        }
      } catch (error) {
        console.error('Failed to fetch term details:', error);
      }
      
      // Fetch financial details
      try {
        const financialsResponse = await fetch(`${apiBaseUrl}/contracts/${contractId}/financials`);
        if (financialsResponse.ok) {
          const financialsData = await financialsResponse.json();
          console.log('Financial details loaded:', financialsData);
          setFinancialDetails(financialsData);
          // Reset pagination to first page when data changes
          setInvoicePage(1);
          setCreditPage(1);
          setPaymentPage(1);
        } else {
          console.error('Failed to fetch financial details - Bad response:', financialsResponse.status);
        }
      } catch (error) {
        console.error('Failed to fetch financial details:', error);
      }
      
      // Fetch upcoming invoice tasks
      try {
        const upcomingInvoicesResponse = await fetch(`${apiBaseUrl}/contracts/${contractId}/upcoming-invoices`);
        if (upcomingInvoicesResponse.ok) {
          const upcomingInvoicesData = await upcomingInvoicesResponse.json();
          console.log('Upcoming invoice tasks loaded:', upcomingInvoicesData);
          setUpcomingInvoices(upcomingInvoicesData);
        } else {
          console.error('Failed to fetch upcoming invoices - Bad response:', upcomingInvoicesResponse.status);
        }
      } catch (error) {
        console.error('Failed to fetch upcoming invoices:', error);
      }
      
      // Fetch documents
      try {
        const documentsResponse = await fetch(`${apiBaseUrl}/documents/contract/${contractId}`);
        if (documentsResponse.ok) {
          const documentsData = await documentsResponse.json();
          setDocuments(documentsData);
        }
      } catch (error) {
        console.error('Failed to fetch documents:', error);
      }
      
      // Fetch resources
      try {
        const resourcesResponse = await fetch(`${apiBaseUrl}/resources/${contractId}`);
        console.log('Resources response status:', resourcesResponse.status);
        if (resourcesResponse.ok) {
          const resourcesData = await resourcesResponse.json();
          console.log('Resources data received:', resourcesData);
          setResources(resourcesData);
        } else {
          console.error('Resources API error:', resourcesResponse.status, resourcesResponse.statusText);
        }
      } catch (error) {
        console.error('Failed to fetch resources:', error);
      }

      // Fetch status report settings (singleton)
      try {
        const settingsRes = await fetch(`${apiBaseUrl}/status-reports/settings/${contractId}`);
        if (settingsRes.ok) {
          const j = await settingsRes.json();
          const s = j?.settings || null;
          setStatusSettings(s);
          if (s) {
            setRecipientsInput(Array.isArray(s.recipients) ? s.recipients.join(', ') : '');
            setSettingsForm({
              enabled: !!s.enabled,
              recipients: Array.isArray(s.recipients) ? s.recipients : [],
              cadence: s.cadence || 'weekly',
              weekly_day: Number.isInteger(s.day_of_week) ? s.day_of_week : 1,
              weekly_time: s.time_of_day || '09:00',
              interval_days: Number.isFinite(s.interval_minutes) ? Math.max(1, Math.round(s.interval_minutes / 1440)) : 14,
              timezone: s.timezone || 'America/New_York',
              lookback_days: s.window_days ?? 14,
              lookahead_days: s.window_forward_days ?? 30
            });
          }
        } else {
          console.error('Failed to fetch contract status settings:', settingsRes.status);
        }
      } catch (error) {
        console.error('Failed to fetch status settings:', error);
      }
      
      // Fetch document types
      try {
        const documentTypesResponse = await fetch(`${apiBaseUrl}/documents/types`);
        if (documentTypesResponse.ok) {
          const documentTypesData = await documentTypesResponse.json();
          setDocumentTypes(documentTypesData);
        }
      } catch (error) {
        console.error('Failed to fetch document types:', error);
      }
      
    } catch (error) {
      console.error('Error fetching contract details:', error);
      setError(`Failed to load contract details: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [contractId]);
  
  // Fetch reference data from the server
  const fetchReferenceData = useCallback(async () => {
    try {
      const apiBaseUrl = 'https://pulse.marketersindemand.com/api';

      // Fetch business units (agencies)
      try {
        const businessUnitsResponse = await fetch(`${apiBaseUrl}/contracts/business-units`);
        if (businessUnitsResponse.ok) {
          const businessUnitsData = await businessUnitsResponse.json();
          // Map to the format needed for dropdowns
          const formattedBusinessUnits = businessUnitsData.map(unit => ({
            value: unit.quickbooks_realm_id, // Store the ID as the value
            label: unit.name // Show the name as the label
          }));
          setBusinessUnits(formattedBusinessUnits);
          console.log('Loaded business units:', formattedBusinessUnits);
        } else {
          console.error('Failed to fetch business units');
        }
      } catch (error) {
        console.error('Error fetching business units:', error);
      }

      // Fetch ClickUp users
      try {
        const usersResponse = await fetch(`${apiBaseUrl}/contracts/clickup-users`);
        if (usersResponse.ok) {
          const usersData = await usersResponse.json();
          // Map to the format needed for dropdowns
          const formattedUsers = usersData.map(user => ({
            value: user.id, // Store the ID as the value
            label: user.full_name // Keep using full_name for dropdowns
          }));
          setClickupUsers(formattedUsers);
          console.log('Loaded ClickUp users:', formattedUsers);
        } else {
          console.error('Failed to fetch ClickUp users');
        }
      } catch (error) {
        console.error('Error fetching ClickUp users:', error);
      }

      // We already have the enumerated types set as initial state values
      // If there's an API to fetch them dynamically, we can add them here
      
    } catch (error) {
      console.error('Error loading reference data:', error);
    }
  }, []);
  
  // Only load contract data on mount
  useEffect(() => {
    fetchContractData();
  }, [fetchContractData]);

  // ---- Status Reports helpers ----
  const cadenceSummary = (s) => {
    if (!s) return '—';
    if ((s.cadence || 'weekly') === 'interval') {
      const days = Number.isFinite(s.interval_days) ? s.interval_days : (Number.isFinite(s.interval_minutes) ? Math.max(1, Math.round(s.interval_minutes / 1440)) : 14);
      return `Every ${days} days at ${s.weekly_time || s.time_of_day || '09:00'} ${s.timezone || ''}`.trim();
    }
    const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const dow = Number.isInteger(s.weekly_day) ? s.weekly_day : (Number.isInteger(s.day_of_week) ? s.day_of_week : 1);
    const time = s.weekly_time || s.time_of_day || '09:00';
    return `Weekly — ${dayNames[(dow+7)%7]} ${time} ${s.timezone || ''}`.trim();
  };

  const handleOpenStatusModal = () => {
    // ensure form is in sync with latest settings
    if (statusSettings) {
      setSettingsForm({
        enabled: !!statusSettings.enabled,
        recipients: Array.isArray(statusSettings.recipients) ? statusSettings.recipients : [],
        cadence: statusSettings.cadence || 'weekly',
        weekly_day: Number.isInteger(statusSettings.day_of_week) ? statusSettings.day_of_week : 1,
        weekly_time: statusSettings.time_of_day || '09:00',
        interval_days: Number.isFinite(statusSettings.interval_minutes) ? Math.max(1, Math.round(statusSettings.interval_minutes / 1440)) : 14,
        timezone: statusSettings.timezone || 'America/New_York',
        lookback_days: statusSettings.window_days ?? 14,
        lookahead_days: statusSettings.window_forward_days ?? 30
      });
      setRecipientsInput(Array.isArray(statusSettings.recipients) ? statusSettings.recipients.join(', ') : '');
    }
    setShowStatusModal(true);
  };
  const handleCloseStatusModal = () => setShowStatusModal(false);

  const setFormField = (key, value) => {
    setSettingsForm(prev => ({ ...prev, [key]: value }));
  };

  const handleRecipientsInput = (value) => {
    setRecipientsInput(value);
    const parts = String(value || '')
      .split(/[,\s;]+/)
      .map(s => s.trim())
      .filter(Boolean);
    setFormField('recipients', parts);
  };

  const saveStatusSettings = async () => {
    try {
      const apiBaseUrl = 'https://pulse.marketersindemand.com/api';
      const body = {
        enabled: !!settingsForm.enabled,
        recipients: Array.isArray(settingsForm.recipients) ? settingsForm.recipients : [],
        cadence: settingsForm.cadence,
        weekly_day: settingsForm.weekly_day,
        weekly_time: settingsForm.weekly_time,
        interval_days: settingsForm.interval_days,
        timezone: settingsForm.timezone,
        lookback_days: settingsForm.lookback_days,
        lookahead_days: settingsForm.lookahead_days
      };
      const res = await fetch(`${apiBaseUrl}/status-reports/settings/${contractId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const msg = await res.text();
        alert(`Failed to save settings: ${res.status} ${msg}`);
        return;
      }
      const j = await res.json();
      setStatusSettings(j.settings);
      setShowStatusModal(false);
    } catch (err) {
      console.error('saveStatusSettings error', err);
      alert('Failed to save settings');
    }
  };

  const sendTestNow = async () => {
    try {
      const apiBaseUrl = 'https://pulse.marketersindemand.com/api';
      const res = await fetch(`${apiBaseUrl}/status-reports/send-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contractId })
      });
      const j = await res.json();
      if (res.ok && j.success) {
        alert('Test report queued/sent.');
      } else {
        alert(`Failed to send: ${j.message || res.status}`);
      }
    } catch (err) {
      console.error('sendTestNow error', err);
      alert('Failed to send test');
    }
  };

  // Handle edit click 
  const handleEditClick = () => {
    if (!isEditMode) {
      // COMPLETELY disable page refresh/reload on tab visibility changes
      disableRefresh();
      console.log('EDIT MODE: COMPLETELY disabling page refresh to prevent data loss on tab switch');
      
      // Also use the context lock as a backup
      lockPageRefresh();
      
      // Initialize the form only once when entering edit mode
      if (contract) {
        console.log('EDIT MODE: Initializing form data');
        
        // Create a complete object with all fields to avoid undefined values
        const initialFormState = {
          contract_name: contract.contract_name ?? contract.contractName ?? '',
          contract_description: contract.contract_description ?? contract.description ?? '',
          contract_status: contract.contract_status ?? contract.status ?? '',
          contract_type: contract.contract_type ?? contract.contractType ?? '',
          amount: contract.amount ?? '',
          monthly_points_allotment: contract.monthly_points_allotment ?? '',
          dollar_per_hour: contract.dollar_per_hour ?? '',
          payment_type: contract.payment_type ?? '',
          autorenewal: !!contract.autorenewal,
          quickbooks_business_unit_id: contract.quickbooks_business_unit_id ?? '',
          account_manager: contract.account_manager ?? '',
          team_manager: contract.team_manager ?? '',
          priority: contract.priority ?? '',
          customer_display_type: contract.customer_display_type ?? '',
          engagement_type: contract.engagement_type ?? '',
          hosting: !!contract.hosting,
          initial_term_length: contract.initial_term_length ?? '',
          subsequent_term_length: contract.subsequent_term_length ?? '',
          notice_period: contract.notice_period ?? '',
          contract_start_date: contract.contract_start_date ?? contract.contractStart ?? '',
          link_to_slack_channel: contract.link_to_slack_channel ?? '',
          deal_id: contract.deal_id ?? '',
          clickup_folder_id: contract.clickup_folder_id ?? ''
        };
        
        // Handle business unit ID mapping
        if (contract.businessUnit && businessUnits.length > 0) {
          const matchingUnit = businessUnits.find(unit => unit.label === contract.businessUnit);
          if (matchingUnit) {
            initialFormState.quickbooks_business_unit_id = matchingUnit.value;
          }
        }
        
        // Set the form state
        setEditedContract(initialFormState);
      }
      
      // Load reference data
      fetchReferenceData();
      
      // Enter edit mode
      setIsEditMode(true);
    }
  };
  
  // Update field values in editedContract state without unnecessary logging
  const handleFieldChange = (field, value) => {
    setEditedContract(prev => ({
      ...prev,
      [field]: value
    }));
    
    // If customer_display_type changes to/from 'hours', update UI immediately
    if (field === 'customer_display_type') {
      console.log('Display type changed to:', value);
    }
  };
  
  // Handle save changes
  const handleSaveChanges = async () => {
    try {
      setLoading(true);
      
      // Find the business unit ID (quickbooks_realm_id) based on the selected unit
      let businessUnitId = editedContract.quickbooks_business_unit_id;
      if (editedContract.businessUnit) {
        // If businessUnit was selected using the dropdown, use it directly as it's already the ID
        businessUnitId = editedContract.businessUnit;
      }
      
      // Track changes for audit logging
      const changes = [];
      
      // Compare old and new values for each field
      if (editedContract.contract_name !== contract.contract_name && 
          editedContract.contract_name !== contract.contractName) {
        changes.push(`Contract Name: "${contract.contract_name || contract.contractName || 'N/A'}" → "${editedContract.contract_name || 'N/A'}"`);
      }
      
      if (editedContract.contract_description !== contract.contract_description) {
        changes.push(`Description: "${contract.contract_description || 'N/A'}" → "${editedContract.contract_description || 'N/A'}"`);
      }
      
      if (editedContract.contract_status !== contract.contract_status && 
          editedContract.contract_status !== contract.status) {
        changes.push(`Status: "${contract.contract_status || contract.status || 'N/A'}" → "${editedContract.contract_status || 'N/A'}"`);
      }
      
      if (editedContract.contract_type !== contract.contract_type && 
          editedContract.contract_type !== contract.contractType) {
        changes.push(`Contract Type: "${contract.contract_type || contract.contractType || 'N/A'}" → "${editedContract.contract_type || 'N/A'}"`);
      }
      
      if (editedContract.amount !== contract.amount) {
        changes.push(`Amount: "${contract.amount || 'N/A'}" → "${editedContract.amount || 'N/A'}"`);
      }
      
      if (editedContract.monthly_points_allotment !== contract.monthly_points_allotment) {
        changes.push(`Monthly Points: "${contract.monthly_points_allotment || 'N/A'}" → "${editedContract.monthly_points_allotment || 'N/A'}"`);
      }
      
      if (editedContract.dollar_per_hour !== contract.dollar_per_hour) {
        changes.push(`Dollar Per Hour: "${contract.dollar_per_hour || 'N/A'}" → "${editedContract.dollar_per_hour || 'N/A'}"`);
      }
      
      if (editedContract.payment_type !== contract.payment_type) {
        changes.push(`Payment Type: "${contract.payment_type || 'N/A'}" → "${editedContract.payment_type || 'N/A'}"`);
      }
      
      if (editedContract.autorenewal !== contract.autorenewal) {
        changes.push(`Autorenewal: "${contract.autorenewal ? 'Yes' : 'No'}" → "${editedContract.autorenewal ? 'Yes' : 'No'}"`);
      }
      
      if (editedContract.account_manager !== contract.account_manager) {
        // Get manager name for display
        const oldManager = contract.accountManagerName || 'N/A';
        let newManager = 'N/A';
        
        if (editedContract.account_manager) {
          const managerOption = clickupUsers.find(user => user.value === editedContract.account_manager);
          if (managerOption) {
            newManager = managerOption.label;
          }
        }
        
        changes.push(`Account Manager: "${oldManager}" → "${newManager}"`);
      }
      
      if (editedContract.team_manager !== contract.team_manager) {
        // Get manager name for display
        const oldManager = contract.teamManagerName || 'N/A';
        let newManager = 'N/A';
        
        if (editedContract.team_manager) {
          const managerOption = clickupUsers.find(user => user.value === editedContract.team_manager);
          if (managerOption) {
            newManager = managerOption.label;
          }
        }
        
        changes.push(`Team Manager: "${oldManager}" → "${newManager}"`);
      }
      
      if (editedContract.priority !== contract.priority) {
        changes.push(`Priority: "${contract.priority || 'N/A'}" → "${editedContract.priority || 'N/A'}"`);
      }
      
      if (editedContract.customer_display_type !== contract.customer_display_type) {
        changes.push(`Display Type: "${contract.customer_display_type || 'N/A'}" → "${editedContract.customer_display_type || 'N/A'}"`);
      }
      
      if (editedContract.engagement_type !== contract.engagement_type) {
        changes.push(`Engagement Type: "${contract.engagement_type || 'N/A'}" → "${editedContract.engagement_type || 'N/A'}"`);
      }
      
      if (editedContract.hosting !== contract.hosting) {
        changes.push(`Hosting: "${contract.hosting ? 'Yes' : 'No'}" → "${editedContract.hosting ? 'Yes' : 'No'}"`);
      }
      
      if (editedContract.initial_term_length !== contract.initial_term_length) {
        changes.push(`Initial Term Length: "${contract.initial_term_length || 'N/A'} days" → "${editedContract.initial_term_length || 'N/A'} days"`);
      }
      
      if (editedContract.subsequent_term_length !== contract.subsequent_term_length) {
        changes.push(`Subsequent Term Length: "${contract.subsequent_term_length || 'N/A'} days" → "${editedContract.subsequent_term_length || 'N/A'} days"`);
      }
      
      if (editedContract.notice_period !== contract.notice_period) {
        changes.push(`Notice Period: "${contract.notice_period || 'N/A'} days" → "${editedContract.notice_period || 'N/A'} days"`);
      }
      
      if (editedContract.contract_start_date !== contract.contract_start_date && 
          editedContract.contract_start_date !== contract.contractStart) {
        changes.push(`Contract Start Date: "${contract.contract_start_date || contract.contractStart || 'N/A'}" → "${editedContract.contract_start_date || 'N/A'}"`);
      }
      
      if (editedContract.link_to_slack_channel !== contract.link_to_slack_channel) {
        changes.push(`Slack Channel: "${contract.link_to_slack_channel || 'N/A'}" → "${editedContract.link_to_slack_channel || 'N/A'}"`);
      }
      
      if (editedContract.deal_id !== contract.deal_id) {
        changes.push(`Deal ID: "${contract.deal_id || 'N/A'}" → "${editedContract.deal_id || 'N/A'}"`);
      }
      
      if (editedContract.clickup_folder_id !== contract.clickup_folder_id) {
        changes.push(`ClickUp Folder ID: "${contract.clickup_folder_id || 'N/A'}" → "${editedContract.clickup_folder_id || 'N/A'}"`);
      }
      
      // Prepare data for update - add dollar_per_hour to contractData
      const contractData = {
        // Basic details
        contract_name: editedContract.contract_name || editedContract.contractName,
        contract_description: editedContract.contract_description,
        contract_status: editedContract.contract_status || editedContract.status,
        contract_type: editedContract.contract_type || editedContract.contractType,
        
        // Financial details
        amount: editedContract.amount !== '' ? editedContract.amount : null,
        monthly_points_allotment: editedContract.monthly_points_allotment !== '' ? parseFloat(editedContract.monthly_points_allotment) : 0,
        dollar_per_hour: editedContract.dollar_per_hour !== '' ? parseFloat(editedContract.dollar_per_hour) : null,
        payment_type: editedContract.payment_type,
        autorenewal: editedContract.autorenewal,
        
        // Term details
        initial_term_length: editedContract.initial_term_length !== '' ? editedContract.initial_term_length : null,
        subsequent_term_length: editedContract.subsequent_term_length !== '' ? editedContract.subsequent_term_length : null, 
        notice_period: editedContract.notice_period !== '' ? editedContract.notice_period : null,
        contract_start_date: editedContract.contract_start_date || editedContract.contractStart,
        
        // Relationship fields - store the IDs
        quickbooks_business_unit_id: businessUnitId,
        account_manager: editedContract.account_manager,
        team_manager: editedContract.team_manager,
        
        // Other settings
        priority: editedContract.priority,
        customer_display_type: editedContract.customer_display_type,
        engagement_type: editedContract.engagement_type,
        hosting: editedContract.hosting,
        
        // Additional fields
        link_to_slack_channel: editedContract.link_to_slack_channel,
        deal_id: editedContract.deal_id,
        clickup_folder_id: editedContract.clickup_folder_id
      };
      
      // Log the data we're sending to the server
      console.log('Sending updated contract data to server:', contractData);
      
      // Call API to update contract
      const apiBaseUrl = 'https://pulse.marketersindemand.com/api';
      const response = await fetch(`${apiBaseUrl}/contracts/${contractId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contractData),
      });
      
      // Update contract with response from server if available
      if (response.ok) {
        const updatedContract = await response.json();
        console.log('Updated contract data received:', updatedContract);
        
        // If we have the updated data, use it - but preserve manager names
        if (updatedContract) {
          setContract({
            ...updatedContract,
            // Always preserve these display names regardless of whether the IDs changed
            accountManagerName: contract.accountManagerName || 'N/A',
            teamManagerName: contract.teamManagerName || 'N/A'
          });
        } else {
          // Otherwise just update our local state with the edited values
          setContract({
            ...contract, 
            ...contractData,
            // Preserve display names for account_manager and team_manager
            accountManagerName: contract.accountManagerName || 'N/A',
            teamManagerName: contract.teamManagerName || 'N/A'
          });
        }
      } else {
        throw new Error(`Failed to update contract: ${response.status}`);
      }
      
      // ALWAYS fetch the manager names after saving
      // Fetch account manager name if one is set
      if (contractData.account_manager) {
        try {
          const apiBaseUrl = 'https://pulse.marketersindemand.com/api';
          const accountManagerResponse = await fetch(`${apiBaseUrl}/clickup/users/${contractData.account_manager}`);
          
          if (accountManagerResponse.ok) {
            const accountManagerData = await accountManagerResponse.json();
            setContract(prevContract => ({
              ...prevContract,
              accountManagerName: accountManagerData.username || 'N/A'
            }));
          }
        } catch (error) {
          console.error('Error fetching account manager name:', error);
        }
      }
      
      // Fetch team manager name if one is set
      if (contractData.team_manager) {
        try {
          const apiBaseUrl = 'https://pulse.marketersindemand.com/api';
          const teamManagerResponse = await fetch(`${apiBaseUrl}/clickup/users/${contractData.team_manager}`);
          
          if (teamManagerResponse.ok) {
            const teamManagerData = await teamManagerResponse.json();
            setContract(prevContract => ({
              ...prevContract,
              teamManagerName: teamManagerData.username || 'N/A'
            }));
          }
        } catch (error) {
          console.error('Error fetching team manager name:', error);
        }
      }
      
      // Exit edit mode
      setIsEditMode(false);
      
      // Re-enable page refresh now that we're done editing
      enableRefresh();
      console.log('EDIT MODE: Re-enabling page refresh after saving changes');
      
      // Also unlock context as backup
      unlockPageRefresh();
      
      // Create audit log entry if there were changes
      if (changes.length > 0) {
        try {
          const apiBaseUrl = 'https://pulse.marketersindemand.com/api';
          const description = changes.join('\n');
          
          // Use the current user's ID or email as the updated_by value
          const updated_by = user?.email || user?.id || 'unknown';
          
          const auditLogData = {
            contract_id: contractId,
            description,
            updated_by
          };
          
          console.log('Creating audit log entry:', auditLogData);
          
          // Call API to create audit log
          const auditLogResponse = await fetch(`${apiBaseUrl}/audit-logs`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(auditLogData),
          });
          
          if (!auditLogResponse.ok) {
            console.error('Failed to create audit log entry:', auditLogResponse.status);
          } else {
            console.log('Audit log entry created successfully');
          }
        } catch (error) {
          console.error('Error creating audit log entry:', error);
        }
      } else {
        console.log('No changes detected, skipping audit log creation');
      }
    } catch (error) {
      console.error('Error saving contract:', error);
      setError(`Failed to save contract: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle cancel edit
  const handleCancelEdit = () => {
    // Simply exit edit mode without saving changes
    setIsEditMode(false);
    
    // Re-enable page refresh now that we're done editing
    enableRefresh();
    console.log('EDIT MODE: Re-enabling page refresh after canceling edit');
    
    // Also unlock context as backup
    unlockPageRefresh();
  };
  
  // Clean up locks when component unmounts
  useEffect(() => {
    return () => {
      // Make sure we re-enable refreshes if the user navigates away while in edit mode
      if (isEditMode) {
        enableRefresh();
        unlockPageRefresh();
        console.log('CLEANUP: Re-enabling page refresh as component unmounts');
      }
    };
  }, [isEditMode, unlockPageRefresh]);

  const renderMainLayoutTitle = () => {
    // Simple breadcrumb without buttons for both view and edit modes
    return (
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
        <Link to="/contracts" className="breadcrumb-link">
          Contracts
        </Link>
        <Typography color="text.primary">
          {!loading && contract ? 
            `${contract.contractName} (${contract.contractNumber})` : 
            'Contract'}
        </Typography>
      </Breadcrumbs>
    );
  };
  
  // Render delivery status badge
  const renderDeliveryStatus = (pointsBurden) => {
    if (pointsBurden === null || pointsBurden === undefined) return 'N/A';
    
    const status = pointsBurden <= 0 ? 'on-track' : 'off-track';
    
    return (
      <div className={`delivery-status ${status}`}>
        {status === 'on-track' ? (
          <>
            <CheckCircleOutlinedIcon fontSize="small" sx={{ marginRight: '4px' }} />
            On Track
          </>
        ) : (
          <>
            <ErrorIcon fontSize="small" sx={{ marginRight: '4px' }} />
            Off Track
          </>
        )}
      </div>
    );
  };

  // Standalone status badge component for dashboard-like display
  const StatusBadge = ({ isOnTrack }) => {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        backgroundColor: isOnTrack ? '#e6f4ea' : '#fce8e6',
        color: isOnTrack ? '#137333' : '#c5221f',
        borderRadius: '16px',
        padding: '4px 12px',
        fontWeight: 'medium',
        width: 'fit-content',
        boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)'
      }}>
        {isOnTrack ? (
          <CheckCircleOutlinedIcon fontSize="small" sx={{ marginRight: '4px' }} />
        ) : (
          <ErrorIcon fontSize="small" sx={{ marginRight: '4px' }} />
        )}
        {isOnTrack ? 'On Track' : 'Off Track'}
      </Box>
    );
  };

  // Helper function to capitalize first letter
  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Helper function to format payment type
  const formatPaymentType = (type) => {
    if (!type) return 'N/A';
    if (type === 'credit_card') return 'Credit Card';
    if (type === 'invoice') return 'Invoice';
    return capitalizeFirstLetter(type);
  };

  // Helper function specifically for contract status formatting
  const formatContractStatus = (status) => {
    if (!status) return 'Unknown';
    
    // Handle possible ENUM values from database schema
    switch(status.toLowerCase()) {
      case 'active':
        return 'Active';
      case 'pending':
        return 'Pending';
      case 'canceled':
        return 'Canceled';
      case 'inactive':
        return 'Inactive';
      default:
        return capitalizeFirstLetter(status);
    }
  };

  // Table header and row renderer for invoices
  const invoicesTableHead = (
    <TableRow>
      <TableCell>Invoice #</TableCell>
      <TableCell>Date</TableCell>
      <TableCell>Due Date</TableCell>
      <TableCell>Amount</TableCell>
      <TableCell>Balance</TableCell>
      <TableCell>Points</TableCell>
      <TableCell>View</TableCell>
    </TableRow>
  );

  const renderInvoiceRow = (invoice, index) => (
    <TableRow key={invoice.id || `invoice-${index}`}>
      <TableCell>{invoice.doc_number || 'N/A'}</TableCell>
      <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
      <TableCell>{formatDate(invoice.due_date)}</TableCell>
      <TableCell>{formatCurrency(invoice.total_amount || 0)}</TableCell>
      <TableCell>{formatCurrency(invoice.balance || 0)}</TableCell>
      <TableCell>{invoice.points || '-'}</TableCell>
      <TableCell>
        {invoice.quickbooks_id ? (
          <a 
            href={`https://pulse.marketersindemand.com/api/quickbooks-pdf/invoices/${invoice.quickbooks_id}`}
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#6200ee', textDecoration: 'none', display: 'flex', alignItems: 'center' }}
          >
            <PdfIcon fontSize="small" />
          </a>
        ) : '-'}
      </TableCell>
    </TableRow>
  );

  // Table header and row renderer for credits
  const creditsTableHead = (
    <TableRow>
      <TableCell>Credit #</TableCell>
      <TableCell>Date</TableCell>
      <TableCell>Amount</TableCell>
      <TableCell>Description</TableCell>
      <TableCell>Points</TableCell>
    </TableRow>
  );

  const renderCreditRow = (credit, index) => (
    <TableRow key={credit.id || `credit-${index}`}>
      <TableCell>{credit.doc_number || 'N/A'}</TableCell>
      <TableCell>{formatDate(credit.credit_date)}</TableCell>
      <TableCell>{formatCurrency(credit.total_amount || 0)}</TableCell>
      <TableCell>
        {credit.raw_data && 
         credit.raw_data.Line && 
         credit.raw_data.Line.some(line => line.Description) 
          ? credit.raw_data.Line.find(line => line.Description)?.Description 
          : credit.memo_data || '-'}
      </TableCell>
      <TableCell>{credit.points || '-'}</TableCell>
    </TableRow>
  );

  // Table header and row renderer for payments
  const paymentsTableHead = (
    <TableRow>
      <TableCell>Payment #</TableCell>
      <TableCell>Date</TableCell>
      <TableCell>Amount</TableCell>
    </TableRow>
  );

  const renderPaymentRow = (payment, index) => (
    <TableRow key={payment.id || `payment-${index}`}>
      <TableCell>{payment.quickbooks_id || 'N/A'}</TableCell>
      <TableCell>{formatDate(payment.payment_date)}</TableCell>
      <TableCell>{formatCurrency(payment.total_amount || 0)}</TableCell>
    </TableRow>
  );

  // Add styling for form controls to ensure visibility
  const formStyles = {
    input: {
      backgroundColor: '#ffffff',
      color: '#000000',
      border: '1px solid #cccccc',
      padding: '8px',
      width: '100%'
    },
    select: {
      backgroundColor: '#ffffff',
      color: '#000000',
      border: '1px solid #cccccc',
      padding: '8px',
      width: '100%'
    }
  };

  // Handle document input change
  const handleDocumentInputChange = (e) => {
    const { name, value } = e.target;
    setNewDocument(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle document type change
  const handleDocumentTypeChange = (event) => {
    setNewDocument(prev => ({
      ...prev,
      contract_document_type: event.target.value
    }));
  };
  
  // Handle add document
  const handleAddDocument = async () => {
    try {
      const apiBaseUrl = 'https://pulse.marketersindemand.com/api';
      
      // Validate required fields
      if (!newDocument.document_url || !newDocument.contract_document_type) {
        alert('Document URL and Type are required');
        return;
      }
      
      const response = await fetch(`${apiBaseUrl}/documents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newDocument)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to add document: ${response.status}`);
      }
      
      // Fetch updated documents
      const documentsResponse = await fetch(`${apiBaseUrl}/documents/contract/${contractId}`);
      if (documentsResponse.ok) {
        const documentsData = await documentsResponse.json();
        setDocuments(documentsData);
      }
      
      // Reset form and close modal
      resetDocumentForm();
      
    } catch (error) {
      console.error('Error adding document:', error);
      alert(`Failed to add document: ${error.message}`);
    }
  };
  
  // Handle delete document
  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }
    
    try {
      const apiBaseUrl = 'https://pulse.marketersindemand.com/api';
      
      const response = await fetch(`${apiBaseUrl}/documents/${documentId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete document: ${response.status}`);
      }
      
      // Update documents list
      setDocuments(documents.filter(doc => doc.id !== documentId));
      
    } catch (error) {
      console.error('Error deleting document:', error);
      alert(`Failed to delete document: ${error.message}`);
    }
  };

  // Handle edit document click
  const handleEditDocument = (document) => {
    setNewDocument({
      id: document.id,
      contract_id: document.contract_id,
      document_url: document.document_url,
      description: document.description || '',
      contract_document_type: document.contract_document_type
    });
    setIsEditingDocument(true);
    setShowAddDocument(true);
  };
  
  // Handle update document
  const handleUpdateDocument = async () => {
    try {
      const apiBaseUrl = 'https://pulse.marketersindemand.com/api';
      
      // Validate required fields
      if (!newDocument.document_url || !newDocument.contract_document_type) {
        alert('Document URL and Type are required');
        return;
      }
      
      const response = await fetch(`${apiBaseUrl}/documents/${newDocument.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newDocument)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update document: ${response.status}`);
      }
      
      // Fetch updated documents
      const documentsResponse = await fetch(`${apiBaseUrl}/documents/contract/${contractId}`);
      if (documentsResponse.ok) {
        const documentsData = await documentsResponse.json();
        setDocuments(documentsData);
      }
      
      // Reset form and close modal
      resetDocumentForm();
      
    } catch (error) {
      console.error('Error updating document:', error);
      alert(`Failed to update document: ${error.message}`);
    }
  };
  
  // Reset document form
  const resetDocumentForm = () => {
    setNewDocument({
      contract_id: contractId,
      document_url: '',
      description: '',
      contract_document_type: ''
    });
    setIsEditingDocument(false);
    setShowAddDocument(false);
  };

  // Handle amendment input change
  const handleAmendmentInputChange = (e) => {
    const { name, value } = e.target;
    setNewAmendment(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle amendment type change
  const handleAmendmentTypeChange = (event) => {
    setNewAmendment(prev => ({
      ...prev,
      amendment_type: event.target.value
    }));
  };
  
  // Handle amendment status change
  const handleAmendmentStatusChange = (event) => {
    setNewAmendment(prev => ({
      ...prev,
      amendment_status: event.target.value
    }));
  };
  
  // Handle add amendment
  const handleAddAmendment = async () => {
    try {
      const apiBaseUrl = 'https://pulse.marketersindemand.com/api';
      
      // Validate required fields
      if (!newAmendment.amendment_type || !newAmendment.amendment_status) {
        alert('Amendment Type and Status are required');
        return;
      }
      
      const response = await fetch(`${apiBaseUrl}/amendments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAmendment)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to add amendment: ${response.status}`);
      }
      
      // Fetch updated amendments
      const amendmentsResponse = await fetch(`${apiBaseUrl}/amendments/contract/${contractId}`);
      if (amendmentsResponse.ok) {
        const amendmentsData = await amendmentsResponse.json();
        setAmendments(amendmentsData);
      }
      
      // Reset form and close modal
      resetAmendmentForm();
      
    } catch (error) {
      console.error('Error adding amendment:', error);
      alert(`Failed to add amendment: ${error.message}`);
    }
  };
  
  // Handle delete amendment
  const handleDeleteAmendment = async (amendmentId) => {
    if (!window.confirm('Are you sure you want to delete this amendment?')) {
      return;
    }
    
    try {
      const apiBaseUrl = 'https://pulse.marketersindemand.com/api';
      
      const response = await fetch(`${apiBaseUrl}/amendments/${amendmentId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete amendment: ${response.status}`);
      }
      
      // Update amendments list
      setAmendments(amendments.filter(a => a.amendment_id !== amendmentId));
      
    } catch (error) {
      console.error('Error deleting amendment:', error);
      alert(`Failed to delete amendment: ${error.message}`);
    }
  };

  // Handle edit amendment click
  const handleEditAmendment = (amendment) => {
    setNewAmendment({
      id: amendment.amendment_id,
      contract_id: amendment.contract_id,
      deal_id: amendment.deal_id || '',
      amendment_description: amendment.amendment_description || '',
      amendment_type: amendment.amendment_type,
      amount: amendment.amount || '',
      amendment_status: amendment.amendment_status || '',
      document_link: amendment.document_link || ''
    });
    setIsEditingAmendment(true);
    setShowAddAmendment(true);
  };
  
  // Handle update amendment
  const handleUpdateAmendment = async () => {
    try {
      const apiBaseUrl = 'https://pulse.marketersindemand.com/api';
      
      // Validate required fields
      if (!newAmendment.amendment_type || !newAmendment.amendment_status) {
        alert('Amendment Type and Status are required');
        return;
      }
      
      const response = await fetch(`${apiBaseUrl}/amendments/${newAmendment.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAmendment)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update amendment: ${response.status}`);
      }
      
      // Fetch updated amendments
      const amendmentsResponse = await fetch(`${apiBaseUrl}/amendments/contract/${contractId}`);
      if (amendmentsResponse.ok) {
        const amendmentsData = await amendmentsResponse.json();
        setAmendments(amendmentsData);
      }
      
      // Reset form and close modal
      resetAmendmentForm();
      
    } catch (error) {
      console.error('Error updating amendment:', error);
      alert(`Failed to update amendment: ${error.message}`);
    }
  };
  
  // Reset amendment form
  const resetAmendmentForm = () => {
    setNewAmendment({
      contract_id: contractId,
      deal_id: '',
      amendment_description: '',
      amendment_type: '',
      amount: '',
      amendment_status: '',
      document_link: ''
    });
    setIsEditingAmendment(false);
    setShowAddAmendment(false);
  };

  // Define the upcoming invoices table headers
  const upcomingInvoicesTableHead = [
    { id: 'name', label: 'Task Name' },
    { id: 'due_date', label: 'Due Date' },
    { id: 'status', label: 'Status' },
    { id: 'points', label: 'Points' },
    { id: 'hours', label: 'Hours' },
    { id: 'amount', label: 'Amount' }
  ];
  
  // Render function for upcoming invoice rows
  const renderUpcomingInvoiceRow = (invoice, index) => (
    <TableRow key={index}>
      <TableCell>{invoice.name}</TableCell>
      <TableCell>{invoice.due_date ? formatDate(invoice.due_date) : 'N/A'}</TableCell>
      <TableCell>{invoice.status || 'N/A'}</TableCell>
      <TableCell>{invoice.points || '0'}</TableCell>
      <TableCell>{invoice.hours || '0'}</TableCell>
      <TableCell>
        {invoice.invoice_amount ? formatCurrency(invoice.invoice_amount) : 'N/A'}
      </TableCell>
    </TableRow>
  );

  // Add a custom tooltip component for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const pointsValue = payload[0].value;
      const isAboveTarget = payload[0].payload.isAboveTarget;
      const targetValue = parseFloat(contract?.monthly_points_allotment || 0);
      const difference = pointsValue - targetValue;
      const percentOfTarget = (pointsValue / targetValue * 100).toFixed(0);
      
      return (
        <div className="chart-tooltip">
          <p className="tooltip-label">{`${label}`}</p>
          <p className="tooltip-value">
            <span style={{ fontWeight: 'bold' }}>Points: </span>
            <span>{pointsValue.toFixed(2)}</span>
          </p>
          <p className="tooltip-value" style={{ color: isAboveTarget ? '#2e7d32' : '#c62828' }}>
            <span style={{ fontWeight: 'bold' }}>Target: </span>
            <span>{targetValue.toFixed(2)}</span>
          </p>
          <p className="tooltip-value" style={{ color: isAboveTarget ? '#2e7d32' : '#c62828' }}>
            <span style={{ fontWeight: 'bold' }}>Difference: </span>
            <span>{difference.toFixed(2)} ({percentOfTarget}%)</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Handle resource input change
  const handleResourceInputChange = (e) => {
    const { name, value } = e.target;
    setNewResource(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle add resource
  const handleAddResource = async () => {
    try {
      const apiBaseUrl = 'https://pulse.marketersindemand.com/api';
      
      // Validate required fields
      if (!newResource.resource_name || !newResource.resource_link) {
        alert('Resource Name and Link are required');
        return;
      }
      
      const response = await fetch(`${apiBaseUrl}/resources`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newResource)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to add resource: ${response.status}`);
      }
      
      // Fetch updated resources
      const resourcesResponse = await fetch(`${apiBaseUrl}/resources/${contractId}`);
      if (resourcesResponse.ok) {
        const resourcesData = await resourcesResponse.json();
        setResources(resourcesData);
      }
      
      // Reset form and close modal
      resetResourceForm();
      
    } catch (error) {
      console.error('Error adding resource:', error);
      alert(`Failed to add resource: ${error.message}`);
    }
  };
  
  // Handle delete resource
  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) {
      return;
    }
    
    try {
      const apiBaseUrl = 'https://pulse.marketersindemand.com/api';
      
      const response = await fetch(`${apiBaseUrl}/resources/${resourceId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete resource: ${response.status}`);
      }
      
      // Update resources list
      setResources(resources.filter(resource => resource.resource_id !== resourceId));
      
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert(`Failed to delete resource: ${error.message}`);
    }
  };

  // Handle edit resource click
  const handleEditResource = (resource) => {
    setNewResource({
      resource_id: resource.resource_id,
      contract_id: resource.contract_id,
      resource_name: resource.resource_name,
      resource_description: resource.resource_description || '',
      resource_link: resource.resource_link
    });
    setIsEditingResource(true);
    setShowAddResource(true);
  };
  
  // Handle update resource
  const handleUpdateResource = async () => {
    try {
      const apiBaseUrl = 'https://pulse.marketersindemand.com/api';
      
      // Validate required fields
      if (!newResource.resource_name || !newResource.resource_link) {
        alert('Resource Name and Link are required');
        return;
      }
      
      const response = await fetch(`${apiBaseUrl}/resources/${newResource.resource_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newResource)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update resource: ${response.status}`);
      }
      
      // Fetch updated resources
      const resourcesResponse = await fetch(`${apiBaseUrl}/resources/${contractId}`);
      if (resourcesResponse.ok) {
        const resourcesData = await resourcesResponse.json();
        setResources(resourcesData);
      }
      
      // Reset form and close modal
      resetResourceForm();
      
    } catch (error) {
      console.error('Error updating resource:', error);
      alert(`Failed to update resource: ${error.message}`);
    }
  };
  
  // Reset resource form
  const resetResourceForm = () => {
    setNewResource({
      contract_id: contractId,
      resource_name: '',
      resource_description: '',
      resource_link: ''
    });
    setIsEditingResource(false);
    setShowAddResource(false);
  };

  return (
    <MainLayout title={renderMainLayoutTitle()}>
      {loading ? (
        <div className="loading-container">
          <CircularProgress />
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <p className="error-details">
            Check the console for more details or contact your administrator.
          </p>
        </div>
      ) : contract ? (
        <div className="contract-details-content">
          
          {/* Title and Edit/Save/Cancel Buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Typography variant="h5" component="div">Overview</Typography>
            {isEditMode ? (
              <div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveChanges}
                  startIcon={<SaveIcon />}
                  sx={{ mr: 1 }}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                  startIcon={<CancelIcon />}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              // Only show edit button for non-team members
              user?.role !== 'team_member' && (
                <Tooltip title="Edit Contract">
                  <IconButton 
                    color="primary" 
                    onClick={handleEditClick}
                    aria-label="edit contract"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )
            )}
          </div>
          
          {/* Basic Info Card */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card className="details-card">
                <CardHeader title="Details" />
                <Divider />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Contract Number</Typography>
                      <Typography variant="body1">{contract.contractNumber || contract.external_id}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Contract Name</Typography>
                      {isEditMode ? (
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={editedContract.contract_name ?? ''}
                          onChange={(e) => handleFieldChange('contract_name', e.target.value)}
                          placeholder="Enter contract name"
                          style={{ backgroundColor: 'white' }}
                          InputProps={{
                            style: { 
                              color: 'black', 
                              backgroundColor: 'white'
                            }
                          }}
                          sx={basicTextFieldStyle}
                        />
                      ) : (
                        <Typography variant="body1">{contract.contractName || contract.contract_name}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Account</Typography>
                      <Typography variant="body1">{contract.accountName}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                      {isEditMode ? (
                        <CustomSelect
                          value={editedContract.contract_type || ''}
                          options={contractTypes}
                          onChange={(e) => handleFieldChange('contract_type', e.target.value)}
                          name="contract_type"
                          style={customSelectStyle}
                          placeholder="Select Contract Type"
                        />
                      ) : (
                        <Typography variant="body1">{capitalizeFirstLetter(contract.contractType || contract.contract_type)}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Business Unit</Typography>
                      {isEditMode ? (
                        <Typography variant="body1" sx={{ mt: 1, fontWeight: 'normal', color: '#555' }}>
                          {contract.businessUnit}
                          <Box sx={{ mt: 0.5, fontSize: '0.75rem', color: '#888' }}>
                            Business unit cannot be changed after contract creation
                          </Box>
                        </Typography>
                      ) : (
                        <Typography variant="body1">{contract.businessUnit}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Priority</Typography>
                      {isEditMode ? (
                        <CustomSelect
                          value={editedContract.priority || ''}
                          options={priorityTiers}
                          onChange={(e) => handleFieldChange('priority', e.target.value)}
                          name="priority"
                          style={customSelectStyle}
                          placeholder="Select Priority"
                        />
                      ) : (
                        <Typography variant="body1">{capitalizeFirstLetter(contract.priority)}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                      {isEditMode ? (
                        <CustomSelect
                          value={editedContract.contract_status || ''}
                          options={contractStatuses}
                          onChange={(e) => handleFieldChange('contract_status', e.target.value)}
                          name="contract_status"
                          style={customSelectStyle}
                          placeholder="Select Status"
                        />
                      ) : (
                        <Typography variant="body1">{formatContractStatus(contract.status || contract.contract_status)}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Display Type</Typography>
                      {isEditMode ? (
                        <CustomSelect
                          value={editedContract.customer_display_type || ''}
                          options={displayTypes}
                          onChange={(e) => handleFieldChange('customer_display_type', e.target.value)}
                          name="customer_display_type"
                          style={customSelectStyle}
                          placeholder="Select Display Type"
                        />
                      ) : (
                        <Typography variant="body1">{capitalizeFirstLetter(contract.customer_display_type)}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                      {isEditMode ? (
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          variant="outlined"
                          size="small"
                          value={editedContract.contract_description || ''}
                          onChange={(e) => handleFieldChange('contract_description', e.target.value)}
                          placeholder="Enter contract description"
                          InputProps={{
                            style: { color: '#000000' }
                          }}
                        />
                      ) : (
                        <Typography variant="body1">{contract.contract_description || 'No description'}</Typography>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Financial Summary Card */}
            <Grid item xs={12} md={6}>
              <Card className="details-card">
                <CardHeader title="Financials" />
                <Divider />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Monthly Amount</Typography>
                      {isEditMode ? (
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          type="number"
                          value={editedContract.amount ?? ''}
                          onChange={(e) => handleFieldChange('amount', parseFloat(e.target.value))}
                          placeholder="Enter monthly amount"
                          style={{ backgroundColor: 'white' }}
                          InputProps={{
                            style: { 
                              color: 'black', 
                              backgroundColor: 'white'
                            },
                            startAdornment: <span style={{ marginRight: 4, color: 'black' }}>$</span>
                          }}
                          sx={basicTextFieldStyle}
                        />
                      ) : (
                        <Typography variant="body1">{formatCurrency(contract.amount)}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Monthly Points</Typography>
                      {isEditMode ? (
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          type="number"
                          inputProps={{ step: "0.01" }}
                          value={editedContract.monthly_points_allotment || ''}
                          onChange={(e) => handleFieldChange('monthly_points_allotment', parseFloat(e.target.value) || 0)}
                          placeholder="Enter monthly points"
                          style={{ backgroundColor: 'white' }}
                          InputProps={{
                            style: { 
                              color: 'black', 
                              backgroundColor: 'white'
                            }
                          }}
                          sx={basicTextFieldStyle}
                        />
                      ) : (
                        <Typography variant="body1">{contract.monthly_points_allotment}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Total Revenue (LTV)</Typography>
                      <Typography variant="body1">{formatCurrency(contract.ltv)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Payment Type</Typography>
                      {isEditMode ? (
                        <CustomSelect
                          value={editedContract.payment_type || ''}
                          options={paymentTypes}
                          onChange={(e) => handleFieldChange('payment_type', e.target.value)}
                          name="payment_type"
                          style={customSelectStyle}
                          placeholder="Select Payment Type"
                        />
                      ) : (
                        <Typography variant="body1">{formatPaymentType(contract.payment_type)}</Typography>
                      )}
                    </Grid>
                    {/* Add Dollar Per Hour field for 'hours' display type */}
                    {(contract.customer_display_type === 'hours' || editedContract.customer_display_type === 'hours') && (
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">Dollar Per Hour</Typography>
                        {isEditMode ? (
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            type="number"
                            inputProps={{ step: "0.01" }}
                            value={editedContract.dollar_per_hour || ''}
                            onChange={(e) => handleFieldChange('dollar_per_hour', parseFloat(e.target.value) || 0)}
                            placeholder="Enter dollar per hour rate"
                            style={{ backgroundColor: 'white' }}
                            InputProps={{
                              style: { 
                                color: 'black', 
                                backgroundColor: 'white'
                              },
                              startAdornment: <span style={{ marginRight: 4, color: 'black' }}>$</span>
                            }}
                            sx={basicTextFieldStyle}
                          />
                        ) : (
                          <Typography variant="body1">{formatCurrency(contract.dollar_per_hour)}</Typography>
                        )}
                      </Grid>
                    )}
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Autorenewal</Typography>
                      {isEditMode ? (
                        <Box sx={autorenewalBoxStyle}>
                          <Switch
                            checked={!!editedContract.autorenewal}
                            onChange={(e) => handleFieldChange('autorenewal', e.target.checked)}
                            color="primary"
                            sx={basicSwitchStyle}
                          />
                          <Typography sx={switchValueTextStyle}>
                            {editedContract.autorenewal ? "Yes" : "No"}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body1">{formatBoolean(contract.autorenewal)}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Delivery Status</Typography>
                      <Typography variant="body1">
                        {pointsSummary ? renderDeliveryStatus(pointsSummary.points_burden) : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Next Invoice Date</Typography>
                      <Typography variant="body1">
                        {contract.next_invoice_date ? formatDate(contract.next_invoice_date) : 
                         upcomingInvoices && upcomingInvoices.length > 0 ? formatDate(upcomingInvoices[0].due_date) : 
                         'No upcoming invoices'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Engagement Type</Typography>
                      {isEditMode ? (
                        <CustomSelect
                          value={editedContract.engagement_type || ''}
                          options={engagementTypes}
                          onChange={(e) => handleFieldChange('engagement_type', e.target.value)}
                          name="engagement_type"
                          style={customSelectStyle}
                          placeholder="Select Engagement Type"
                        />
                      ) : (
                        <Typography variant="body1">{capitalizeFirstLetter(contract.engagement_type)}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Hosting Included</Typography>
                      {isEditMode ? (
                        <Box sx={autorenewalBoxStyle}>
                          <Switch
                            checked={!!editedContract.hosting}
                            onChange={(e) => handleFieldChange('hosting', e.target.checked)}
                            color="primary"
                            sx={basicSwitchStyle}
                          />
                          <Typography sx={switchValueTextStyle}>
                            {editedContract.hosting ? "Yes" : "No"}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body1">{formatBoolean(contract.hosting)}</Typography>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Term Information Card */}
            <Grid item xs={12}>
              <Card className="details-card">
                <CardHeader title="Term Information" />
                <Divider />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TableContainer component={Paper} variant="outlined">
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Term</TableCell>
                              <TableCell>Value</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>Contract Start Date</TableCell>
                              <TableCell>
                                {isEditMode ? (
                                  <TextField
                                    type="date"
                                    size="small"
                                    value={editedContract.contract_start_date ? 
                                      new Date(editedContract.contract_start_date)
                                        .toISOString().split('T')[0] : 
                                      ''}
                                    onChange={(e) => handleFieldChange('contract_start_date', e.target.value)}
                                    InputProps={{
                                      style: {
                                        color: '#000000',
                                        backgroundColor: 'white',
                                        opacity: 1
                                      }
                                    }}
                                    sx={basicTextFieldStyle}
                                  />
                                ) : (
                                  formatDate(contract.contractStart || contract.contract_start_date)
                                )}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Initial Term?</TableCell>
                              <TableCell>
                                {/* Always display this as read-only since it's calculated in a database view */}
                                {formatBoolean(contract.isInitialTerm)}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Initial Term Length</TableCell>
                              <TableCell>
                                {isEditMode ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={editedContract.initial_term_length ?? ''}
                                    onChange={(e) => handleFieldChange('initial_term_length', parseInt(e.target.value, 10) || 0)}
                                    InputProps={{
                                      style: {
                                        color: '#000000',
                                        backgroundColor: 'white',
                                        opacity: 1
                                      },
                                      endAdornment: <span style={{ marginLeft: 4, color: 'black' }}>days</span>
                                    }}
                                    sx={basicTextFieldStyle}
                                  />
                                ) : (
                                  `${contract.initial_term_length} days`
                                )}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Subsequent Term Length</TableCell>
                              <TableCell>
                                {isEditMode ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={editedContract.subsequent_term_length ?? ''}
                                    onChange={(e) => handleFieldChange('subsequent_term_length', parseInt(e.target.value, 10) || 0)}
                                    InputProps={{
                                      style: {
                                        color: '#000000',
                                        backgroundColor: 'white',
                                        opacity: 1
                                      },
                                      endAdornment: <span style={{ marginLeft: 4, color: 'black' }}>days</span>
                                    }}
                                    sx={basicTextFieldStyle}
                                  />
                                ) : (
                                  `${contract.subsequent_term_length} days`
                                )}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Notice Period</TableCell>
                              <TableCell>
                                {isEditMode ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={editedContract.notice_period ?? ''}
                                    onChange={(e) => handleFieldChange('notice_period', parseInt(e.target.value, 10) || 0)}
                                    InputProps={{
                                      style: {
                                        color: '#000000',
                                        backgroundColor: 'white',
                                        opacity: 1
                                      },
                                      endAdornment: <span style={{ marginLeft: 4, color: 'black' }}>days</span>
                                    }}
                                    sx={basicTextFieldStyle}
                                  />
                                ) : (
                                  `${contract.notice_period} days`
                                )}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TableContainer component={Paper} variant="outlined">
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Current & Next Terms</TableCell>
                              <TableCell>Date</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>Current Term Start</TableCell>
                              <TableCell>{formatDate(contract.currentTermStart)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Current Term End</TableCell>
                              <TableCell>{formatDate(contract.currentTermEnd)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Next Term Renewal</TableCell>
                              <TableCell>{formatDate(contract.nextTermRenewal)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Next Term Start</TableCell>
                              <TableCell>{formatDate(contract.nextTermStart)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Next Term End</TableCell>
                              <TableCell>{formatDate(contract.nextTermEnd)}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Points Summary Card */}
            <Grid item xs={12} md={6}>
              <Card className="details-card">
                <CardHeader 
                  title="Points Summary" 
                />
                <Divider />
                {pointsSummary && (
                  <Box sx={{ padding: '12px 16px 0', display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle1" sx={{ mr: 2 }}>Status:</Typography>
                    <StatusBadge isOnTrack={pointsSummary.points_burden <= 0} />
                  </Box>
                )}
                <CardContent>
                  {pointsSummary ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Metric</TableCell>
                            <TableCell>Value</TableCell>
                            {contract.customer_display_type === 'hours' && (
                              <TableCell>Hours</TableCell>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>Monthly Points Allotment</TableCell>
                            <TableCell>
                              {isEditMode ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ step: "0.01" }}
                                  value={editedContract.monthly_points_allotment || ''}
                                  onChange={(e) => handleFieldChange('monthly_points_allotment', parseFloat(e.target.value) || 0)}
                                  placeholder="Enter monthly points"
                                  style={{ backgroundColor: 'white' }}
                                  InputProps={{
                                    style: { color: 'black', backgroundColor: 'white' }
                                  }}
                                  sx={basicTextFieldStyle}
                                />
                              ) : (
                                parseFloat(contract.monthly_points_allotment).toFixed(2)
                              )}
                            </TableCell>
                            {contract.customer_display_type === 'hours' && (
                              <TableCell>
                                {contract.dollar_per_hour ? 
                                  ((parseFloat(contract.monthly_points_allotment) / parseFloat(contract.dollar_per_hour)) * 100).toFixed(2) : 
                                  'N/A'}
                              </TableCell>
                            )}
                          </TableRow>
                          <TableRow>
                            <TableCell>Points Purchased</TableCell>
                            <TableCell>{pointsSummary.points_purchased ? parseFloat(pointsSummary.points_purchased).toFixed(2) : '0.00'}</TableCell>
                            {contract.customer_display_type === 'hours' && (
                              <TableCell>
                                {contract.dollar_per_hour && pointsSummary.points_purchased ? 
                                  ((parseFloat(pointsSummary.points_purchased) / parseFloat(contract.dollar_per_hour)) * 100).toFixed(2) : 
                                  '0.00'}
                              </TableCell>
                            )}
                          </TableRow>
                          <TableRow>
                            <TableCell>Points Credited</TableCell>
                            <TableCell>{pointsSummary.points_credited ? parseFloat(pointsSummary.points_credited).toFixed(2) : '0.00'}</TableCell>
                            {contract.customer_display_type === 'hours' && (
                              <TableCell>
                                {contract.dollar_per_hour && pointsSummary.points_credited ? 
                                  ((parseFloat(pointsSummary.points_credited) / parseFloat(contract.dollar_per_hour)) * 100).toFixed(2) : 
                                  '0.00'}
                              </TableCell>
                            )}
                          </TableRow>
                          <TableRow>
                            <TableCell>Points Delivered</TableCell>
                            <TableCell>{pointsSummary.points_delivered ? parseFloat(pointsSummary.points_delivered).toFixed(2) : '0.00'}</TableCell>
                            {contract.customer_display_type === 'hours' && (
                              <TableCell>
                                {contract.dollar_per_hour && pointsSummary.points_delivered ? 
                                  ((parseFloat(pointsSummary.points_delivered) / parseFloat(contract.dollar_per_hour)) * 100).toFixed(2) : 
                                  '0.00'}
                              </TableCell>
                            )}
                          </TableRow>
                          <TableRow>
                            <TableCell>Points Balance (excluding "Working")</TableCell>
                            <TableCell>{pointsSummary.points_balance ? parseFloat(pointsSummary.points_balance).toFixed(2) : '0.00'}</TableCell>
                            {contract.customer_display_type === 'hours' && (
                              <TableCell>
                                {contract.dollar_per_hour && pointsSummary.points_balance ? 
                                  ((parseFloat(pointsSummary.points_balance) / parseFloat(contract.dollar_per_hour)) * 100).toFixed(2) : 
                                  '0.00'}
                              </TableCell>
                            )}
                          </TableRow>
                          <TableRow>
                            <TableCell>Points Burden</TableCell>
                            <TableCell>{pointsSummary.points_burden ? parseFloat(pointsSummary.points_burden).toFixed(2) : '0.00'}</TableCell>
                            {contract.customer_display_type === 'hours' && (
                              <TableCell>
                                {contract.dollar_per_hour && pointsSummary.points_burden ? 
                                  ((parseFloat(pointsSummary.points_burden) / parseFloat(contract.dollar_per_hour)) * 100).toFixed(2) : 
                                  '0.00'}
                              </TableCell>
                            )}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body1">No points data available</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            {/* Additional Information Card */}
            <Grid item xs={12} md={6}>
              <Card className="details-card">
                <CardHeader title="Additional Information" />
                <Divider />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Account Manager</Typography>
                      {isEditMode ? (
                        <CustomSelect
                          value={editedContract.account_manager || ''}
                          options={clickupUsers}
                          onChange={(e) => handleFieldChange('account_manager', e.target.value)}
                          name="account_manager"
                          style={customSelectStyle}
                          disabled={!clickupUsers.length}
                          placeholder="Select Account Manager"
                        />
                      ) : (
                        <Typography variant="body1">{contract.accountManagerName || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Team Manager</Typography>
                      {isEditMode ? (
                        <CustomSelect
                          value={editedContract.team_manager || ''}
                          options={clickupUsers}
                          onChange={(e) => handleFieldChange('team_manager', e.target.value)}
                          name="team_manager"
                          style={customSelectStyle}
                          disabled={!clickupUsers.length}
                          placeholder="Select Team Manager"
                        />
                      ) : (
                        <Typography variant="body1">{contract.teamManagerName || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Deal ID</Typography>
                      {isEditMode ? (
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={editedContract.deal_id ?? ''}
                          onChange={(e) => handleFieldChange('deal_id', e.target.value)}
                          InputProps={{
                            style: {
                              color: '#000000',
                              backgroundColor: 'white',
                              opacity: 1
                            }
                          }}
                          sx={basicTextFieldStyle}
                        />
                      ) : (
                        <Typography variant="body1">
                          {contract.deal_id ? (
                            <a 
                              href={`https://app.hubspot.com/contacts/46815402/record/0-3/${contract.deal_id}`}
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: '#6200ee', textDecoration: 'none' }}
                            >
                              {contract.deal_id}
                            </a>
                          ) : 'N/A'}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">ClickUp Folder</Typography>
                      {isEditMode ? (
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={editedContract.clickup_folder_id ?? ''}
                          onChange={(e) => handleFieldChange('clickup_folder_id', e.target.value)}
                          InputProps={{
                            style: {
                              color: '#000000',
                              backgroundColor: 'white',
                              opacity: 1
                            }
                          }}
                          sx={basicTextFieldStyle}
                        />
                      ) : (
                        <Typography variant="body1">
                          {contract.clickup_folder_id ? (
                            <a 
                              href={`https://app.clickup.com/14292505/v/o/f/${contract.clickup_folder_id}`}
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: '#6200ee', textDecoration: 'underline', display: 'flex', alignItems: 'center' }}
                            >
                              View Folder
                              <NavigateNextIcon fontSize="small" sx={{ ml: 0.5 }} />
                            </a>
                          ) : 'N/A'}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Slack Channel</Typography>
                      {isEditMode ? (
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={editedContract.link_to_slack_channel ?? ''}
                          onChange={(e) => handleFieldChange('link_to_slack_channel', e.target.value)}
                          InputProps={{
                            style: {
                              color: '#000000',
                              backgroundColor: 'white',
                              opacity: 1
                            }
                          }}
                          sx={basicTextFieldStyle}
                        />
                      ) : (
                        <Typography variant="body1">
                          {contract.link_to_slack_channel ? (
                            <a href={contract.link_to_slack_channel} target="_blank" rel="noopener noreferrer">
                              {contract.link_to_slack_channel}
                            </a>
                          ) : 'N/A'}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
                      <Typography variant="body1">{formatDate(contract.created_at)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Last Updated</Typography>
                      <Typography variant="body1">{formatDate(contract.updated_at)}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Point Production Chart */}
            <Grid item xs={12}>
              <Card className="details-card">
                <CardHeader 
                  title="Point Production" 
                  subheader="Monthly points delivered compared to monthly allotment target"
                />
                <Divider />
                <CardContent sx={{ padding: '20px 10px 20px 0' }}>
                  {pointsProductionData.length > 0 && contract?.monthly_points_allotment ? (
                    <div style={{ width: '100%', height: 400 }}>
                      <ResponsiveContainer>
                        <AreaChart
                          data={pointsProductionData}
                          margin={{ top: 20, right: 30, left: 30, bottom: 30 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="name" 
                            label={{ 
                              value: 'Month', 
                              position: 'insideBottomRight', 
                              offset: -10,
                              fill: '#666',
                              fontSize: 12
                            }}
                          />
                          <YAxis 
                            label={{ 
                              value: 'Points', 
                              angle: -90, 
                              position: 'insideLeft',
                              style: { textAnchor: 'middle' },
                              fill: '#666',
                              fontSize: 12
                            }}
                          />
                          <RechartsTooltip content={<CustomTooltip />} />
                          <Legend formatter={(value) => 'Monthly Points Delivered'} />
                          <defs>
                            <linearGradient id="colorPoints" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#8A42EC" stopOpacity={0.8}/>
                              <stop offset="95%" stopColor="#8A42EC" stopOpacity={0.1}/>
                            </linearGradient>
                          </defs>
                          <ReferenceLine 
                            y={parseFloat(contract.monthly_points_allotment)} 
                            stroke="#000" 
                            strokeDasharray="3 3"
                            label={{ 
                              value: `Monthly Target: ${parseFloat(contract.monthly_points_allotment).toFixed(2)}`, 
                              position: 'insideBottomRight',
                              fill: '#000',
                              fontSize: 12
                            }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="points" 
                            name="Monthly Points Delivered"
                            stroke="#8A42EC" 
                            fillOpacity={1} 
                            fill="url(#colorPoints)" 
                            activeDot={{ 
                              r: 8,
                              stroke: "#fff",
                              strokeWidth: 2,
                              fill: (dataObj) => dataObj.payload.isAboveTarget ? "#2e7d32" : "#c62828"
                            }}
                            dot={{
                              r: 4,
                              stroke: "#fff",
                              strokeWidth: 1,
                              fill: (dataObj) => dataObj.payload.isAboveTarget ? "#2e7d32" : "#c62828"
                            }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <Typography variant="body1" sx={{ textAlign: 'center', padding: '40px 0' }}>No point production data available</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            {/* Status Reports Card */}
            <Grid item xs={12}>
              <Card className="details-card">
                <CardHeader 
                  title="Status Reports" 
                  action={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button variant="outlined" size="small" onClick={sendTestNow}>
                        Send Test Now
                      </Button>
                      <Button variant="contained" size="small" onClick={handleOpenStatusModal}>
                        Manage
                      </Button>
                    </Box>
                  }
                />
                <Divider />
                <CardContent>
                  {statusSettings ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TableContainer component={Paper} variant="outlined">
                          <Table>
                            <TableBody>
                              <TableRow>
                                <TableCell>Enabled</TableCell>
                                <TableCell>{statusSettings.enabled ? 'Yes' : 'No'}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Recipients</TableCell>
                                <TableCell>
                                  {Array.isArray(statusSettings.recipients) && statusSettings.recipients.length
                                    ? statusSettings.recipients.join(', ')
                                    : '—'}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Cadence</TableCell>
                                <TableCell>{cadenceSummary({
                                  cadence: statusSettings.cadence,
                                  weekly_day: statusSettings.day_of_week,
                                  weekly_time: statusSettings.time_of_day,
                                  interval_minutes: statusSettings.interval_minutes,
                                  timezone: statusSettings.timezone
                                })}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Lookback / Lookahead</TableCell>
                                <TableCell>{(statusSettings.window_days ?? 14)} / {(statusSettings.window_forward_days ?? 30)} days</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TableContainer component={Paper} variant="outlined">
                          <Table>
                            <TableBody>
                              <TableRow>
                                <TableCell>Last Sent</TableCell>
                                <TableCell>{statusSettings.last_sent_at ? formatDate(statusSettings.last_sent_at) : '—'}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Next Run</TableCell>
                                <TableCell>{statusSettings.next_run_at ? formatDate(statusSettings.next_run_at) : '—'}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Timezone</TableCell>
                                <TableCell>{statusSettings.timezone || '—'}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="body1">No settings yet. Click Manage to configure.</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            {/* Contract Goals Table - Only show for strategic contracts */}
            {contract && contract.engagement_type === 'strategic' && (
              <Grid item xs={12}>
                <Card className="details-card">
                  <CardHeader 
                    title="Contract Goals" 
                    subheader="Current active goals for this contract"
                  />
                  <Divider />
                  <CardContent>
                    {contractGoals && contractGoals.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell width="40%">Goal</TableCell>
                              <TableCell width="20%">Due Date</TableCell>
                              <TableCell width="40%">Progress</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {contractGoals.map((goal) => (
                              <TableRow key={goal.task_id}>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {goal.goal_name}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {formatDate(goal.due_date)}
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <div className="goals-progress-container">
                                      <div 
                                        className="goals-progress-bar" 
                                        style={{ width: `${goal.percent_complete || 0}%` }}
                                      />
                                    </div>
                                    <span className="goals-progress-text">
                                      {`${goal.percent_complete || 0}%`}
                                    </span>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body1" sx={{ textAlign: 'center', padding: '40px 0' }}>
                        No active goals for this contract
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}
            
            {/* Documents Card */}
            <Grid item xs={12}>
              <Card className="details-card">
                <CardHeader 
                  title="Documents" 
                  action={
                    <Button 
                      variant="contained" 
                      color="primary" 
                      size="small"
                      onClick={() => setShowAddDocument(true)}
                    >
                      Add Document
                    </Button>
                  }
                />
                <Divider />
                <CardContent>
                  {documents.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Type</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Link</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {documents.map((document) => (
                            <TableRow key={document.id}>
                              <TableCell>{document.contract_document_type}</TableCell>
                              <TableCell>{document.description || '-'}</TableCell>
                              <TableCell>
                                <a 
                                  href={document.document_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ color: '#6200ee', textDecoration: 'underline', display: 'flex', alignItems: 'center' }}
                                >
                                  View Document
                                  <NavigateNextIcon fontSize="small" sx={{ ml: 0.5 }} />
                                </a>
                              </TableCell>
                              <TableCell>{formatDate(document.created_at)}</TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEditDocument(document)}
                                  sx={{ mr: 1 }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteDocument(document.id)}
                                >
                                  <CancelOutlinedIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body1">No documents for this contract</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            {/* Amendments Card */}
            <Grid item xs={12}>
              <Card className="details-card">
                <CardHeader 
                  title="Amendments" 
                  action={
                    <Button 
                      variant="contained" 
                      color="primary" 
                      size="small"
                      onClick={() => setShowAddAmendment(true)}
                    >
                      Add Amendment
                    </Button>
                  }
                />
                <Divider />
                <CardContent>
                  {amendments.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Deal ID</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Document</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {amendments.map((amendment) => (
                            <TableRow key={amendment.amendment_id}>
                              <TableCell>
                                {amendment.deal_id ? (
                                  <a 
                                    href={`https://app.hubspot.com/contacts/46815402/record/0-3/${amendment.deal_id}`}
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    style={{ color: '#6200ee', textDecoration: 'underline' }}
                                  >
                                    {amendment.deal_id}
                                  </a>
                                ) : '-'}
                              </TableCell>
                              <TableCell>{capitalizeFirstLetter(amendment.amendment_type)}</TableCell>
                              <TableCell>{amendment.amendment_description || '-'}</TableCell>
                              <TableCell>{formatCurrency(amendment.amount)}</TableCell>
                              <TableCell>{amendment.amendment_status}</TableCell>
                              <TableCell>
                                {amendment.document_link ? (
                                  <a 
                                    href={amendment.document_link} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    style={{ color: '#6200ee', textDecoration: 'underline', display: 'flex', alignItems: 'center' }}
                                  >
                                    View Document
                                    <NavigateNextIcon fontSize="small" sx={{ ml: 0.5 }} />
                                  </a>
                                ) : '-'}
                              </TableCell>
                              <TableCell>{formatDate(amendment.created_at)}</TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEditAmendment(amendment)}
                                  sx={{ mr: 1 }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteAmendment(amendment.amendment_id)}
                                >
                                  <CancelOutlinedIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body1">No amendments for this contract</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            {/* Resources Card */}
            <Grid item xs={12}>
              <Card className="details-card">
                <CardHeader 
                  title="Resources" 
                  action={
                    <Button 
                      variant="contained" 
                      color="primary" 
                      size="small"
                      onClick={() => setShowAddResource(true)}
                    >
                      Add Resource
                    </Button>
                  }
                />
                <Divider />
                <CardContent>
                  {resources.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Link</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {resources.map((resource) => (
                            <TableRow key={resource.resource_id}>
                              <TableCell>{resource.resource_name}</TableCell>
                              <TableCell>{resource.resource_description || '-'}</TableCell>
                              <TableCell>
                                <a 
                                  href={resource.resource_link} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ color: '#6200ee', textDecoration: 'underline', display: 'flex', alignItems: 'center' }}
                                >
                                  View Resource
                                  <NavigateNextIcon fontSize="small" sx={{ ml: 0.5 }} />
                                </a>
                              </TableCell>
                              <TableCell>{formatDate(resource.created_at)}</TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEditResource(resource)}
                                  sx={{ mr: 1 }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteResource(resource.resource_id)}
                                >
                                  <CancelOutlinedIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body1">No resources for this contract</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            {/* Upcoming Invoices Card */}
            <Grid item xs={12}>
              <Card className="details-card">
                <CardHeader title="Upcoming Invoices" />
                <Divider />
                <CardContent>
                  {upcomingInvoices && upcomingInvoices.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {upcomingInvoicesTableHead.map((column) => (
                              <TableCell key={column.id}>
                                {column.label}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {upcomingInvoices.map((invoice, index) => renderUpcomingInvoiceRow(invoice, index))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body1">No upcoming invoices for this contract</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            {/* Financial Details Card - Invoices, Credits, Payments */}
            <Grid item xs={12}>
              <Card className="details-card">
                <CardHeader title="Financial Transactions" />
                <Divider />
                <CardContent>
                  <Typography variant="h6" gutterBottom>Invoices</Typography>
                  <PaginatedTable 
                    data={financialDetails.invoices || []}
                    tableHead={invoicesTableHead}
                    renderRow={renderInvoiceRow}
                    rowsPerPage={10}
                    emptyMessage="No invoices found"
                  />

                  <Typography variant="h6" gutterBottom>Credits</Typography>
                  <PaginatedTable 
                    data={financialDetails.credits || []}
                    tableHead={creditsTableHead}
                    renderRow={renderCreditRow}
                    rowsPerPage={10}
                    emptyMessage="No credits found"
                  />

                  <Typography variant="h6" gutterBottom>Payments</Typography>
                  <PaginatedTable 
                    data={financialDetails.payments || []}
                    tableHead={paymentsTableHead}
                    renderRow={renderPaymentRow}
                    rowsPerPage={10}
                    emptyMessage="No payments found"
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </div>
      ) : (
        <div className="not-found-container">
          <Typography variant="h5">Contract not found</Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => navigate('/contracts')}
            sx={{ mt: 2 }}
          >
            Return to Contracts
          </Button>
        </div>
      )}
      
      {/* Add debugging panel at the bottom of the "scrollable-content contract-details-content" div: */}
      <Grid item xs={12}>
        <Button 
          variant="outlined" 
          color="primary" 
          onClick={() => setShowDebug(!showDebug)}
          sx={{ mt: 2 }}
        >
          {showDebug ? "Hide Debug Info" : "Show Debug Info"}
        </Button>
        
        {isEditMode && (
          <Card className="details-card" sx={{ mt: 2, mb: 4, border: '2px solid blue' }}>
            <CardHeader title="FORM STATE VERIFICATION" />
            <Divider />
            <CardContent>
              <Typography variant="h6">Contract Name: "{editedContract.contract_name || '[empty]'}"</Typography>
              <Typography variant="h6">Description: "{editedContract.contract_description || '[empty]'}"</Typography>
              <Typography variant="h6">Monthly Amount: "{editedContract.amount || '[empty]'}"</Typography>
              <Typography variant="h6">Monthly Points: "{editedContract.monthly_points_allotment || '[empty]'}"</Typography>
              <Typography variant="h6">Status: "{editedContract.contract_status || '[empty]'}"</Typography>
              
              <Typography variant="body1" sx={{ mt: 2, color: 'blue' }}>
                If you can see values above but not in the form fields, there's a rendering issue with the MUI components.
              </Typography>
            </CardContent>
          </Card>
        )}
        
        {isEditMode && (
          <Card className="details-card" sx={{ mt: 2, mb: 4, border: '2px solid red' }}>
            <CardHeader title="CONTRACT TEXT FIELD DEBUG VIEW" />
            <Divider />
            <CardContent>
              <Typography variant="h6">Contract Name Value: "{editedContract.contract_name || '[empty]'}"</Typography>
              <Typography variant="h6">Description Value: "{editedContract.contract_description || '[empty]'}"</Typography>
              <Typography variant="h6">Monthly Amount: "{editedContract.amount || '[empty]'}"</Typography>
              
              <Button 
                variant="contained" 
                color="error" 
                onClick={() => alert(JSON.stringify(editedContract, null, 2))}
                sx={{ mt: 2 }}
              >
                ALERT ALL VALUES
              </Button>
            </CardContent>
          </Card>
        )}
        
        {showDebug && (
          <Card className="details-card" sx={{ mt: 2, mb: 4 }}>
            <CardHeader title="Debug Information" />
            <Divider />
            <CardContent>
              <Typography variant="h6">Edit Mode: {isEditMode ? "ON" : "OFF"}</Typography>
              
              <Typography variant="h6" sx={{ mt: 2 }}>Account and Team Manager Data:</Typography>
              <Typography>Account Manager ID: {contract.account_manager || '[empty]'}</Typography>
              <Typography>Account Manager Name: {contract.accountManagerName || '[empty]'}</Typography>
              <Typography>Team Manager ID: {contract.team_manager || '[empty]'}</Typography>
              <Typography>Team Manager Name: {contract.teamManagerName || '[empty]'}</Typography>
              
              <Typography variant="h6" sx={{ mt: 2 }}>Contract Data:</Typography>
              <pre style={{ overflow: 'auto', maxHeight: '200px' }}>
                {JSON.stringify(contract, null, 2)}
              </pre>
              
              <Typography variant="h6" sx={{ mt: 2 }}>Edited Contract Data:</Typography>
              <pre style={{ overflow: 'auto', maxHeight: '200px' }}>
                {JSON.stringify(editedContract, null, 2)}
              </pre>
              
              <Typography variant="h6" sx={{ mt: 2 }}>Reference Data:</Typography>
              <Typography variant="subtitle1">Business Units: {businessUnits.length} items</Typography>
              <pre style={{ overflow: 'auto', maxHeight: '100px' }}>
                {JSON.stringify(businessUnits, null, 2)}
              </pre>
              
              <Typography variant="subtitle1">ClickUp Users: {clickupUsers.length} items</Typography>
              <pre style={{ overflow: 'auto', maxHeight: '100px' }}>
                {JSON.stringify(clickupUsers, null, 2)}
              </pre>
            </CardContent>
          </Card>
        )}
      </Grid>
      
      {/* Add/Edit Document Modal - Simplified version with direct styling */}
      <Dialog
        open={showAddDocument}
        onClose={resetDocumentForm}
        aria-labelledby="document-title"
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor: 'white',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            borderRadius: '8px',
            overflow: 'hidden'
          }
        }}
      >
        <div style={{ 
          padding: '16px 24px', 
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e0e0e0',
          fontWeight: 'bold',
          fontSize: '18px'
        }}>
          {isEditingDocument ? 'Edit Document' : 'Add Document'}
        </div>
        
        <div style={{ padding: '24px' }}>
          {/* Document Type */}
          <div style={{ marginBottom: '20px' }}>
            <label 
              htmlFor="document-type" 
              style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500',
                color: '#333'
              }}
            >
              Document Type *
            </label>
            <select
              id="document-type"
              value={newDocument.contract_document_type}
              onChange={handleDocumentTypeChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #8A42EC',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: 'black',
                fontSize: '14px',
                height: '42px'
              }}
              required
            >
              <option value="" disabled>Select document type</option>
              {documentTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Document URL */}
          <div style={{ marginBottom: '20px' }}>
            <label 
              htmlFor="document-url" 
              style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500',
                color: '#333'
              }}
            >
              Document URL *
            </label>
            <input
              id="document-url"
              name="document_url"
              type="text"
              value={newDocument.document_url}
              onChange={handleDocumentInputChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #8A42EC',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: 'black',
                fontSize: '14px'
              }}
              required
            />
            <span style={{ fontSize: '12px', color: '#666', marginTop: '4px', display: 'block' }}>
              Enter the Google Drive or cloud storage URL
            </span>
          </div>
          
          {/* Description */}
          <div style={{ marginBottom: '10px' }}>
            <label 
              htmlFor="description" 
              style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500',
                color: '#333'
              }}
            >
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={newDocument.description}
              onChange={handleDocumentInputChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #8A42EC',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: 'black',
                fontSize: '14px',
                minHeight: '100px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>
        </div>
        
        <div style={{ 
          padding: '16px 24px', 
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={resetDocumentForm}
            style={{
              padding: '8px 16px',
              border: 'none',
              background: 'none',
              marginRight: '12px',
              cursor: 'pointer',
              color: '#8A42EC',
              fontWeight: '500'
            }}
          >
            Cancel
          </button>
          <button
            onClick={isEditingDocument ? handleUpdateDocument : handleAddDocument}
            disabled={!newDocument.document_url || !newDocument.contract_document_type}
            style={{
              padding: '8px 20px',
              backgroundColor: '#8A42EC',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontWeight: '500',
              cursor: (!newDocument.document_url || !newDocument.contract_document_type) ? 'not-allowed' : 'pointer',
              opacity: (!newDocument.document_url || !newDocument.contract_document_type) ? 0.7 : 1
            }}
          >
            {isEditingDocument ? 'Update' : 'Save'}
          </button>
        </div>
      </Dialog>

      {/* Manage Status Reports Modal */}
      <Dialog
        open={showStatusModal}
        onClose={handleCloseStatusModal}
        aria-labelledby="status-reports-title"
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor: 'white',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            borderRadius: '8px',
            overflow: 'hidden'
          }
        }}
      >
        <div style={{ 
          padding: '16px 24px', 
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e0e0e0',
          fontWeight: 'bold',
          fontSize: '18px'
        }}>
          Manage Status Reports
        </div>

        <div style={{ padding: '24px' }}>
          {/* Enabled */}
          <div style={{ marginBottom: '16px' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!settingsForm.enabled}
                  onChange={(e) => setFormField('enabled', e.target.checked)}
                  sx={basicSwitchStyle}
                />
              }
              label="Enabled"
            />
          </div>

          {/* Recipients */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#333' }}>Recipients</label>
            <input
              type="text"
              value={recipientsInput}
              onChange={(e) => handleRecipientsInput(e.target.value)}
              placeholder="a@x.com, b@y.com"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #8A42EC',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: 'black',
                fontSize: '14px',
                height: '42px'
              }}
            />
            <span style={{ fontSize: '12px', color: '#666', marginTop: '4px', display: 'block' }}>
              Separate multiple emails with commas, spaces, or semicolons.
            </span>
          </div>

          {/* Cadence */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#333' }}>Cadence</label>
            <select
              value={settingsForm.cadence || 'weekly'}
              onChange={(e) => setFormField('cadence', e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #8A42EC',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: 'black',
                fontSize: '14px',
                height: '42px'
              }}
            >
              <option value="weekly">Weekly</option>
              <option value="interval">Interval</option>
            </select>
          </div>

          {/* Weekly controls */}
          {settingsForm.cadence !== 'interval' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#333' }}>Day of week</label>
                <select
                  value={settingsForm.weekly_day}
                  onChange={(e) => setFormField('weekly_day', parseInt(e.target.value, 10))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #8A42EC',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    color: 'black',
                    fontSize: '14px',
                    height: '42px'
                  }}
                >
                  <option value={0}>Sunday</option>
                  <option value={1}>Monday</option>
                  <option value={2}>Tuesday</option>
                  <option value={3}>Wednesday</option>
                  <option value={4}>Thursday</option>
                  <option value={5}>Friday</option>
                  <option value={6}>Saturday</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#333' }}>Time (HH:mm)</label>
                <input
                  type="time"
                  value={settingsForm.weekly_time}
                  onChange={(e) => setFormField('weekly_time', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #8A42EC',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    color: 'black',
                    fontSize: '14px',
                    height: '42px'
                  }}
                />
              </div>
            </div>
          )}

          {/* Interval controls */}
          {settingsForm.cadence === 'interval' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#333' }}>Every (days)</label>
                <input
                  type="number"
                  min={1}
                  value={settingsForm.interval_days}
                  onChange={(e) => setFormField('interval_days', Math.max(1, parseInt(e.target.value || '1', 10)))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #8A42EC',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    color: 'black',
                    fontSize: '14px',
                    height: '42px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#333' }}>Time (HH:mm)</label>
                <input
                  type="time"
                  value={settingsForm.weekly_time}
                  onChange={(e) => setFormField('weekly_time', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #8A42EC',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    color: 'black',
                    fontSize: '14px',
                    height: '42px'
                  }}
                />
              </div>
            </div>
          )}

          {/* Timezone */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#333' }}>Timezone (IANA)</label>
            <input
              type="text"
              value={settingsForm.timezone}
              onChange={(e) => setFormField('timezone', e.target.value)}
              placeholder="America/New_York"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #8A42EC',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: 'black',
                fontSize: '14px',
                height: '42px'
              }}
            />
          </div>

          {/* Lookback / Lookahead */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#333' }}>Lookback (days)</label>
              <input
                type="number"
                min={1}
                value={settingsForm.lookback_days}
                onChange={(e) => setFormField('lookback_days', Math.max(1, parseInt(e.target.value || '1', 10)))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #8A42EC',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  color: 'black',
                  fontSize: '14px',
                  height: '42px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#333' }}>Lookahead (days)</label>
              <input
                type="number"
                min={0}
                value={settingsForm.lookahead_days}
                onChange={(e) => setFormField('lookahead_days', Math.max(0, parseInt(e.target.value || '0', 10)))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #8A42EC',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  color: 'black',
                  fontSize: '14px',
                  height: '42px'
                }}
              />
            </div>
          </div>
        </div>

        <div style={{ 
          padding: '16px 24px', 
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={handleCloseStatusModal}
            style={{
              padding: '8px 16px',
              border: 'none',
              background: 'none',
              marginRight: '12px',
              cursor: 'pointer',
              color: '#8A42EC',
              fontWeight: '500'
            }}
          >
            Cancel
          </button>
          <button
            onClick={saveStatusSettings}
            style={{
              padding: '8px 20px',
              backgroundColor: '#8A42EC',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Save
          </button>
        </div>
      </Dialog>
      
      {/* Add/Edit Amendment Modal - Simplified version with direct styling */}
      <Dialog
        open={showAddAmendment}
        onClose={resetAmendmentForm}
        aria-labelledby="amendment-title"
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor: 'white',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            borderRadius: '8px',
            overflow: 'hidden'
          }
        }}
      >
        <div style={{ 
          padding: '16px 24px', 
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e0e0e0',
          fontWeight: 'bold',
          fontSize: '18px'
        }}>
          {isEditingAmendment ? 'Edit Amendment' : 'Add Amendment'}
        </div>
        
        <div style={{ padding: '24px' }}>
          {/* Deal ID */}
          <div style={{ marginBottom: '20px' }}>
            <label 
              htmlFor="deal-id" 
              style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500',
                color: '#333'
              }}
            >
              Deal ID (Hubspot ID)
            </label>
            <input
              id="deal-id"
              name="deal_id"
              type="text"
              value={newAmendment.deal_id}
              onChange={handleAmendmentInputChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #8A42EC',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: 'black',
                fontSize: '14px'
              }}
            />
          </div>
          
          {/* Amendment Type */}
          <div style={{ marginBottom: '20px' }}>
            <label 
              htmlFor="amendment-type" 
              style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500',
                color: '#333'
              }}
            >
              Amendment Type *
            </label>
            <select
              id="amendment-type"
              value={newAmendment.amendment_type}
              onChange={handleAmendmentTypeChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #8A42EC',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: 'black',
                fontSize: '14px',
                height: '42px'
              }}
              required
            >
              <option value="" disabled>Select amendment type</option>
              <option value="recurring">Recurring</option>
              <option value="project">Project</option>
            </select>
          </div>
          
          {/* Amount */}
          <div style={{ marginBottom: '20px' }}>
            <label 
              htmlFor="amount" 
              style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500',
                color: '#333'
              }}
            >
              Amount
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{ 
                position: 'absolute', 
                left: '12px', 
                top: '10px',
                color: 'black'
              }}>$</span>
              <input
                id="amount"
                name="amount"
                type="number"
                value={newAmendment.amount}
                onChange={handleAmendmentInputChange}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 24px',
                  border: '1px solid #8A42EC',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  color: 'black',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
          
          {/* Status */}
          <div style={{ marginBottom: '20px' }}>
            <label 
              htmlFor="amendment-status" 
              style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500',
                color: '#333'
              }}
            >
              Status *
            </label>
            <select
              id="amendment-status"
              value={newAmendment.amendment_status}
              onChange={handleAmendmentStatusChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #8A42EC',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: 'black',
                fontSize: '14px',
                height: '42px'
              }}
              required
            >
              <option value="" disabled>Select status</option>
              <option value="pending">Pending</option>
              <option value="signed">Signed</option>
            </select>
          </div>
          
          {/* Document Link */}
          <div style={{ marginBottom: '20px' }}>
            <label 
              htmlFor="document-link" 
              style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500',
                color: '#333'
              }}
            >
              Document Link
            </label>
            <input
              id="document-link"
              name="document_link"
              type="text"
              value={newAmendment.document_link}
              onChange={handleAmendmentInputChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #8A42EC',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: 'black',
                fontSize: '14px'
              }}
            />
            <span style={{ fontSize: '12px', color: '#666', marginTop: '4px', display: 'block' }}>
              Enter the Google Drive or cloud storage URL
            </span>
          </div>
          
          {/* Description */}
          <div style={{ marginBottom: '10px' }}>
            <label 
              htmlFor="amendment-description" 
              style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500',
                color: '#333'
              }}
            >
              Description
            </label>
            <textarea
              id="amendment-description"
              name="amendment_description"
              value={newAmendment.amendment_description}
              onChange={handleAmendmentInputChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #8A42EC',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: 'black',
                fontSize: '14px',
                minHeight: '100px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>
        </div>
        
        <div style={{ 
          padding: '16px 24px', 
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={resetAmendmentForm}
            style={{
              padding: '8px 16px',
              border: 'none',
              background: 'none',
              marginRight: '12px',
              cursor: 'pointer',
              color: '#8A42EC',
              fontWeight: '500'
            }}
          >
            Cancel
          </button>
          <button
            onClick={isEditingAmendment ? handleUpdateAmendment : handleAddAmendment}
            disabled={!newAmendment.amendment_type || !newAmendment.amendment_status}
            style={{
              padding: '8px 20px',
              backgroundColor: '#8A42EC',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontWeight: '500',
              cursor: (!newAmendment.amendment_type || !newAmendment.amendment_status) ? 'not-allowed' : 'pointer',
              opacity: (!newAmendment.amendment_type || !newAmendment.amendment_status) ? 0.7 : 1
            }}
          >
            {isEditingAmendment ? 'Update' : 'Save'}
          </button>
        </div>
      </Dialog>
      
      {/* Add/Edit Resource Modal - Simplified version with direct styling */}
      <Dialog
        open={showAddResource}
        onClose={resetResourceForm}
        aria-labelledby="resource-title"
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor: 'white',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            borderRadius: '8px',
            overflow: 'hidden'
          }
        }}
      >
        <div style={{ 
          padding: '16px 24px', 
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e0e0e0',
          fontWeight: 'bold',
          fontSize: '18px'
        }}>
          {isEditingResource ? 'Edit Resource' : 'Add Resource'}
        </div>
        
        <div style={{ padding: '24px' }}>
          {/* Resource Name */}
          <div style={{ marginBottom: '20px' }}>
            <label 
              htmlFor="resource-name" 
              style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500',
                color: '#333'
              }}
            >
              Resource Name *
            </label>
            <input
              id="resource-name"
              name="resource_name"
              type="text"
              value={newResource.resource_name}
              onChange={handleResourceInputChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #8A42EC',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: 'black',
                fontSize: '14px'
              }}
              required
            />
          </div>
          
          {/* Resource Link */}
          <div style={{ marginBottom: '20px' }}>
            <label 
              htmlFor="resource-link" 
              style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500',
                color: '#333'
              }}
            >
              Resource Link *
            </label>
            <input
              id="resource-link"
              name="resource_link"
              type="text"
              value={newResource.resource_link}
              onChange={handleResourceInputChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #8A42EC',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: 'black',
                fontSize: '14px'
              }}
              required
            />
            <span style={{ fontSize: '12px', color: '#666', marginTop: '4px', display: 'block' }}>
              Enter the URL of the external resource
            </span>
          </div>
          
          {/* Description */}
          <div style={{ marginBottom: '10px' }}>
            <label 
              htmlFor="resource-description" 
              style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500',
                color: '#333'
              }}
            >
              Description
            </label>
            <textarea
              id="resource-description"
              name="resource_description"
              value={newResource.resource_description}
              onChange={handleResourceInputChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #8A42EC',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: 'black',
                fontSize: '14px',
                minHeight: '100px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>
        </div>
        
        <div style={{ 
          padding: '16px 24px', 
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={resetResourceForm}
            style={{
              padding: '8px 16px',
              border: 'none',
              background: 'none',
              marginRight: '12px',
              cursor: 'pointer',
              color: '#8A42EC',
              fontWeight: '500'
            }}
          >
            Cancel
          </button>
          <button
            onClick={isEditingResource ? handleUpdateResource : handleAddResource}
            disabled={!newResource.resource_name || !newResource.resource_link}
            style={{
              padding: '8px 20px',
              backgroundColor: '#8A42EC',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontWeight: '500',
              cursor: (!newResource.resource_name || !newResource.resource_link) ? 'not-allowed' : 'pointer',
              opacity: (!newResource.resource_name || !newResource.resource_link) ? 0.7 : 1
            }}
          >
            {isEditingResource ? 'Update' : 'Save'}
          </button>
        </div>
      </Dialog>
    </MainLayout>
  );
}; 